import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import {
  Send,
  Plus,
  FileText,
  CheckCircle,
  User,
  UserCog,
  Users,
  MapPin,
  Phone,
  Mail,
  Download,
  Sparkles,
  Star,
  Zap,
  Crown,
  Loader2,
  ArrowRight,
  HelpCircle,
  ChevronDown,
  Clock,
  Eye,
  AlertCircle,
  ExternalLink,
  UserPlus,
  Trash2,
  Pencil,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Printer,
  Copy,
  HeartHandshake,
  BrushCleaning,
  ChefHat,
  ClipboardCheck,
  GraduationCap,
  Activity,
  KeyRound,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import StaffDocumentiDialog from "../components/personale/StaffDocumentiDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** standard = turni da 7 ore (default); sei_ore = turni da 6 ore */
const SCHEMA_ORARIO_OPTIONS = [
  {
    value: "standard",
    label: "7 ore",
    description: "Mattina 7-14 · Pomeriggio 14-21 · Notte 21-7",
  },
  {
    value: "sei_ore",
    label: "6 ore",
    description: "Mattina 7-13 · Pomeriggio 13-19 · Notte 19-7",
  },
];

const DEFAULT_TURNI_CONFIG = {
  schema_orario: "standard",
  max_notti_consecutive: 3,
  riposo_dopo_notti: 1,
  orari: {
    mattina: { inizio: "07:00", fine: "14:00" },
    pomeriggio: { inizio: "14:00", fine: "21:00" },
    notte: { inizio: "21:00", fine: "07:00" },
    lunga: { inizio: "07:00", fine: "21:00" },
  },
  pomeriggio_corto: { enabled: false, ora_inizio: "14:00", ora_fine: "20:00" },
};

const formatTurnoRange = (range) => {
  if (!range?.inizio || !range?.fine) return "";
  return `${range.inizio.replace(":00", "")}-${range.fine.replace(":00", "")}`;
};

const mergeTurniConfig = (config) => ({
  ...DEFAULT_TURNI_CONFIG,
  ...(config || {}),
  orari: {
    ...DEFAULT_TURNI_CONFIG.orari,
    ...(config?.orari || {}),
  },
  pomeriggio_corto: {
    ...DEFAULT_TURNI_CONFIG.pomeriggio_corto,
    ...(config?.pomeriggio_corto || {}),
  },
});

// Rileva se siamo su dispositivo mobile/tablet
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
};

// FAQ items
const faqItems = [
  {
    question: "Perché al momento non visualizzo candidati disponibili?",
    answer: [
      "I profili mostrati dipendono dalle candidature effettivamente ricevute e compatibili con la zona, il ruolo e il periodo.",
      "Affidya non utilizza un database statico, ma organizza candidature reali e attive."
    ]
  },
  {
    question: "Affidya non ha un database di candidati?",
    answer: [
      "Affidya utilizza un sistema dinamico di raccolta e selezione.",
      "I profili arrivano da candidature dirette, annunci pubblicati sui canali di ricerca lavoro e segnalazioni professionali."
    ]
  },
  {
    question: "Perché vedo pochi profili nella mia zona?",
    answer: [
      "La disponibilità del personale socio-assistenziale varia molto in base al territorio e al periodo.",
      "Il sistema mostra solo profili compatibili entro il raggio geografico impostato."
    ]
  },
  {
    question: "Cosa succede se non ci sono candidati disponibili?",
    answer: [
      "La richiesta viene comunque presa in carico.",
      "Il sistema attiva la ricerca attraverso i canali disponibili e segnala eventuali nuovi profili compatibili non appena disponibili."
    ]
  },
  {
    question: "Il piano a pagamento garantisce candidati?",
    answer: [
      "No. I piani a pagamento attivano priorità, canali di ricerca aggiuntivi e tempi di gestione più rapidi, ma non garantiscono la disponibilità immediata di profili."
    ]
  },
  {
    question: "Qual è la differenza tra Base e Pro se i candidati dipendono dal territorio?",
    answer: [
      "Il piano Pro garantisce: priorità nella presa in carico delle richieste, attivazione annunci su più canali, selezione avanzata dei profili compatibili e tempi di risposta più rapidi.",
      "La disponibilità resta legata al mercato del lavoro locale."
    ]
  },
  {
    question: "Posso inserire più richieste se non vedo candidati?",
    answer: [
      "Sì. È possibile inserire più richieste per ruoli diversi o periodi differenti, aumentando le possibilità di intercettare profili compatibili."
    ]
  },
  {
    question: "In quanto tempo arrivano i primi profili?",
    answer: [
      "I tempi possono variare in base a: area geografica, ruolo richiesto e periodo dell'anno.",
      "Quando sono disponibili profili compatibili, vengono mostrati direttamente in piattaforma."
    ]
  },
  {
    question: "Posso essere avvisato quando arriva un nuovo candidato?",
    answer: [
      "Sì. Il sistema aggiorna lo stato della richiesta e segnala la disponibilità di nuovi profili compatibili."
    ]
  },
  {
    question: "Affidya sostituisce le agenzie di selezione?",
    answer: [
      "Affidya non è un'agenzia tradizionale, ma una piattaforma che struttura e coordina il processo di ricerca e selezione del personale, riducendo tempi e costi operativi."
    ]
  },
  {
    question: "Perché Affidya non mostra profili non compatibili?",
    answer: [
      "Per garantire qualità e trasparenza, il sistema mostra solo profili coerenti con i criteri impostati (ruolo, distanza, disponibilità)."
    ]
  }
];

// Stati delle richieste
const STAFF_FIGURA_OSS = "oss";
const STAFF_FIGURA_PULIZIE = "pulizie";
const STAFF_FIGURA_CUOCO = "cuoco";
const STAFF_FIGURA_EDUCATORE = "educatore";
const STAFF_FIGURA_TERAPISTA = "terapista";
const TURNO_ATTIVITA_OCCUPAZIONALE = "attivita_occupazionale";

const EDUCATORE_SPECIALIZZAZIONI = [
  { value: "professionale", label: "Educatore professionale" },
  { value: "socio_pedagogico", label: "Educatore socio-pedagogico" },
  { value: "altro", label: "Altro" },
];
const TERAPISTA_SPECIALIZZAZIONI = [
  { value: "occupazionale", label: "Terapista occupazionale" },
  { value: "della_riabilitazione", label: "Terapista della riabilitazione" },
  { value: "fisioterapista", label: "Fisioterapista" },
  { value: "logopedista", label: "Logopedista" },
  { value: "psicomotricista", label: "Terapista della psicomotricità" },
  { value: "altro", label: "Altro" },
];
const SPECIALIZZAZIONI_BY_FIGURA = {
  [STAFF_FIGURA_EDUCATORE]: EDUCATORE_SPECIALIZZAZIONI,
  [STAFF_FIGURA_TERAPISTA]: TERAPISTA_SPECIALIZZAZIONI,
};

const staffFigura = (person) => person?.figura || STAFF_FIGURA_OSS;
const isOssFigura = (person) => staffFigura(person) === STAFF_FIGURA_OSS;
const isFiguraAttivita = (personOrFigura) => {
  const f = typeof personOrFigura === "string" ? personOrFigura : staffFigura(personOrFigura);
  return f === STAFF_FIGURA_EDUCATORE || f === STAFF_FIGURA_TERAPISTA;
};
/** Pulizie/Cuoco: email opzionale (solo per invio turni); nessuna area riservata né codice univoco. */
const emailOptionalForFigura = (personOrFigura) => {
  const f = typeof personOrFigura === "string" ? personOrFigura : staffFigura(personOrFigura);
  return f === STAFF_FIGURA_PULIZIE || f === STAFF_FIGURA_CUOCO;
};
const staffFiguraLabel = (figura) =>
  ({
    [STAFF_FIGURA_OSS]: "OSS",
    [STAFF_FIGURA_PULIZIE]: "Pulizie",
    [STAFF_FIGURA_CUOCO]: "Cuoco",
    [STAFF_FIGURA_EDUCATORE]: "Educatore",
    [STAFF_FIGURA_TERAPISTA]: "Terapista",
  }[figura] || "OSS");
const staffSpecializzazioneOptions = (figura) => SPECIALIZZAZIONI_BY_FIGURA[figura] || [];

const STAFF_FIGURA_THEME = {
  [STAFF_FIGURA_OSS]: {
    label: "OSS",
    Icon: HeartHandshake,
    borderColor: "#0ea5e9",
    borderClass: "border-l-sky-500",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-600",
    cardBorder: "border-sky-200",
    cardGradient: "from-sky-50 to-white",
    titleColor: "text-sky-700",
    descColor: "text-sky-600",
    btnClass: "bg-sky-600 hover:bg-sky-700",
    spinnerColor: "text-sky-500"
  },
  [STAFF_FIGURA_PULIZIE]: {
    label: "Pulizie",
    Icon: BrushCleaning,
    borderColor: "#14b8a6",
    borderClass: "border-l-teal-500",
    iconBg: "bg-teal-100",
    iconColor: "text-teal-700",
    cardBorder: "border-teal-200",
    cardGradient: "from-teal-50 to-white",
    titleColor: "text-teal-800",
    descColor: "text-teal-700",
    btnClass: "bg-teal-600 hover:bg-teal-700",
    spinnerColor: "text-teal-500"
  },
  [STAFF_FIGURA_CUOCO]: {
    label: "Cuoco",
    Icon: ChefHat,
    borderColor: "#f97316",
    borderClass: "border-l-orange-500",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-700",
    cardBorder: "border-orange-200",
    cardGradient: "from-orange-50 to-white",
    titleColor: "text-orange-800",
    descColor: "text-orange-700",
    btnClass: "bg-orange-600 hover:bg-orange-700",
    spinnerColor: "text-orange-500"
  },
  [STAFF_FIGURA_EDUCATORE]: {
    label: "Educatore",
    Icon: GraduationCap,
    borderColor: "#6366f1",
    borderClass: "border-l-indigo-500",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-700",
    cardBorder: "border-indigo-200",
    cardGradient: "from-indigo-50 to-white",
    titleColor: "text-indigo-800",
    descColor: "text-indigo-700",
    btnClass: "bg-indigo-600 hover:bg-indigo-700",
    spinnerColor: "text-indigo-500"
  },
  [STAFF_FIGURA_TERAPISTA]: {
    label: "Terapista",
    Icon: Activity,
    borderColor: "#059669",
    borderClass: "border-l-emerald-600",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-700",
    cardBorder: "border-emerald-200",
    cardGradient: "from-emerald-50 to-white",
    titleColor: "text-emerald-800",
    descColor: "text-emerald-700",
    btnClass: "bg-emerald-600 hover:bg-emerald-700",
    spinnerColor: "text-emerald-500"
  }
};

const getStaffFiguraTheme = (figura) =>
  STAFF_FIGURA_THEME[figura || STAFF_FIGURA_OSS] || STAFF_FIGURA_THEME[STAFF_FIGURA_OSS];

function StaffFiguraIcon({ figura, className = "w-3.5 h-3.5 shrink-0" }) {
  const theme = getStaffFiguraTheme(figura);
  const Icon = theme.Icon;
  return <Icon className={`${theme.iconColor} ${className}`} aria-hidden />;
}

const STATI_RICHIESTA = {
  "inviata": { label: "Inviata", color: "bg-blue-100 text-blue-700", icon: Send },
  "vista": { label: "Visionata", color: "bg-amber-100 text-amber-700", icon: Eye },
  "lavorazione": { label: "In lavorazione", color: "bg-violet-100 text-violet-700", icon: Clock },
  "cv_inviato": { label: "CV inviati", color: "bg-emerald-100 text-emerald-700", icon: FileText },
  "completata": { label: "Completata", color: "bg-green-100 text-green-700", icon: CheckCircle },
};

export default function Personale() {
  const { token, hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [candidati, setCandidati] = useState([]);
  const [loadingCandidati, setLoadingCandidati] = useState(false);
  const [comuneStruttura, setComuneStruttura] = useState("");
  const [raggioKm, setRaggioKm] = useState(30);
  
  // Piano e richieste
  const [userPlan, setUserPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // Modals
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCandidato, setSelectedCandidato] = useState(null);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showCvModal, setShowCvModal] = useState(false);
  const [cvUrl, setCvUrl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [cvFileName, setCvFileName] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Form nuova richiesta
  const [newRequest, setNewRequest] = useState({
    ruolo: "",
    descrizione: "",
    urgenza: "normale",
    orario: "",
    data_inizio: ""
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  // OSS (Operatori Socio-Sanitari) - per futuro calendario turni
  const [ossList, setOssList] = useState([]);
  const [loadingOss, setLoadingOss] = useState(false);
  const [showOssModal, setShowOssModal] = useState(false);
  const [editingOssId, setEditingOssId] = useState(null);
  const [submittingOss, setSubmittingOss] = useState(false);
  const [ossForm, setOssForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    password: "",
    telefono: "",
    figura: STAFF_FIGURA_OSS,
    specializzazione: "",
    specializzazione_altro: "",
    invia_email_accesso: true,
    auth_mode: "email",
    login_code: "",
    regenerate_login_code: false,
    responsabile: false,
  });
  const [ossCodeReveal, setOssCodeReveal] = useState(null); // { nome, cognome, login_code } dopo creazione/rigenerazione
  const [documentiStaff, setDocumentiStaff] = useState(null);

  const ossOnlyList = useMemo(() => ossList.filter((o) => isOssFigura(o)), [ossList]);
  const pulizieList = useMemo(
    () => ossList.filter((o) => staffFigura(o) === STAFF_FIGURA_PULIZIE),
    [ossList]
  );
  const cuocoList = useMemo(() => ossList.filter((o) => staffFigura(o) === STAFF_FIGURA_CUOCO), [ossList]);
  const educatoreList = useMemo(
    () => ossList.filter((o) => staffFigura(o) === STAFF_FIGURA_EDUCATORE),
    [ossList]
  );
  const terapistaList = useMemo(
    () => ossList.filter((o) => staffFigura(o) === STAFF_FIGURA_TERAPISTA),
    [ossList]
  );
  const listByFigura = useMemo(
    () => ({
      [STAFF_FIGURA_PULIZIE]: pulizieList,
      [STAFF_FIGURA_CUOCO]: cuocoList,
      [STAFF_FIGURA_EDUCATORE]: educatoreList,
      [STAFF_FIGURA_TERAPISTA]: terapistaList,
    }),
    [pulizieList, cuocoList, educatoreList, terapistaList]
  );
  const calendarioTurniSections = useMemo(
    () =>
      [
        { key: STAFF_FIGURA_OSS, rows: ossOnlyList },
        { key: STAFF_FIGURA_PULIZIE, rows: pulizieList },
        { key: STAFF_FIGURA_CUOCO, rows: cuocoList },
        { key: STAFF_FIGURA_EDUCATORE, rows: educatoreList },
        { key: STAFF_FIGURA_TERAPISTA, rows: terapistaList },
      ].filter((s) => s.rows.length > 0),
    [ossOnlyList, pulizieList, cuocoList, educatoreList, terapistaList]
  );
  // Calendario turni OSS (vista mensile)
  const [turni, setTurni] = useState([]);
  const [ferie, setFerie] = useState([]);
  const [turniConfig, setTurniConfig] = useState(DEFAULT_TURNI_CONFIG);
  const [loadingTurni, setLoadingTurni] = useState(false);
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 7) + "-01";
  });
  const [showTurnoModal, setShowTurnoModal] = useState(false);
  const [turnoModalData, setTurnoModalData] = useState({ data: null, turno: null });
  const [selectedOssIdsForTurno, setSelectedOssIdsForTurno] = useState([]);
  const [showFerieModal, setShowFerieModal] = useState(false);
  const [ferieForm, setFerieForm] = useState({ oss_id: "", from_date: "", to_date: "", note: "" });
  const [savingFerie, setSavingFerie] = useState(false);
  // Modale modifica turno per cella (OSS x giorno) della vista matrice
  const [showCellTurnoModal, setShowCellTurnoModal] = useState(false);
  const [cellTurnoData, setCellTurnoData] = useState({ ossId: null, data: null });
  const [savingCellTurno, setSavingCellTurno] = useState(false);
  // Modifiche turni non ancora comunicate agli OSS: { ossId: [ { type, data, turno }, ... ] }
  const [pendingTurnoChanges, setPendingTurnoChanges] = useState({});
  const [sendingTurnoSummary, setSendingTurnoSummary] = useState(false);
  const [clearingMonthTurni, setClearingMonthTurni] = useState(false);
  const [clearingRowOssId, setClearingRowOssId] = useState(null);
  /** Conferma UI (dialog) per svuotamento mese o riga calendario turni */
  const [bulkClearDialog, setBulkClearDialog] = useState(null);
  const [sendingTurniMeseEmail, setSendingTurniMeseEmail] = useState(false);
  // Modale Genera turni del mese
  const [showGeneraTurniModal, setShowGeneraTurniModal] = useState(false);
  const [generaMonth, setGeneraMonth] = useState("");
  const [generaOssIds, setGeneraOssIds] = useState([]);
  const [generaSostituisci, setGeneraSostituisci] = useState(true);
  const [generaUsaEsistentiBase, setGeneraUsaEsistentiBase] = useState(false);
  const [generaMaxNotti, setGeneraMaxNotti] = useState(3);
  const [generaRiposo, setGeneraRiposo] = useState(1);
  const [generaTurniMattina, setGeneraTurniMattina] = useState(1);
  const [generaTurniPomeriggio, setGeneraTurniPomeriggio] = useState(1);
  const [generaTurniNotte, setGeneraTurniNotte] = useState(1);
  const [generaCoperturaFestivi, setGeneraCoperturaFestivi] = useState(false);
  const [generaTurniMattinaFestivo, setGeneraTurniMattinaFestivo] = useState(1);
  const [generaTurniPomeriggioFestivo, setGeneraTurniPomeriggioFestivo] = useState(1);
  const [generaTurniNotteFestivo, setGeneraTurniNotteFestivo] = useState(1);
  const [generaTurniLungheFestivo, setGeneraTurniLungheFestivo] = useState(0);
  const [generaTurniLunghe, setGeneraTurniLunghe] = useState(0);
  const [generaRiposoDopoLunga, setGeneraRiposoDopoLunga] = useState(1);
  const [generaOssIdsLunghe, setGeneraOssIdsLunghe] = useState([]);
  const [generaOssIdsAssentiDomenica, setGeneraOssIdsAssentiDomenica] = useState([]);
  const [generaOssIdsSoloMattina, setGeneraOssIdsSoloMattina] = useState([]);
  const [generaOssIdsSoloPomeriggio, setGeneraOssIdsSoloPomeriggio] = useState([]);
  const [generaOssIdsSoloNotte, setGeneraOssIdsSoloNotte] = useState([]);
  const [generaAvanzate, setGeneraAvanzate] = useState(false);
  const [generaModalita, setGeneraModalita] = useState("copertura");
  const [generaUsaIstruzioni, setGeneraUsaIstruzioni] = useState(false);
  const [generaIstruzioniNote, setGeneraIstruzioniNote] = useState("");
  const [loadingGenera, setLoadingGenera] = useState(false);
  const [turniTemplates, setTurniTemplates] = useState([]);
  const [selectedTurniTemplateId, setSelectedTurniTemplateId] = useState("");
  const [turniTemplateName, setTurniTemplateName] = useState("");
  const [savingTurniTemplate, setSavingTurniTemplate] = useState(false);
  const [turniAutoDialogMode, setTurniAutoDialogMode] = useState("genera");
  const [showControllaRisultatiModal, setShowControllaRisultatiModal] = useState(false);
  const [controllaRisultati, setControllaRisultati] = useState(null);
  const [loadingControlla, setLoadingControlla] = useState(false);
  const [showGeneraAttesaModal, setShowGeneraAttesaModal] = useState(false);
  const [regolaMaxNottiCalendario, setRegolaMaxNottiCalendario] = useState(3);
  const [regolaRiposoNottiCalendario, setRegolaRiposoNottiCalendario] = useState(1);
  const [savingTurniConfig, setSavingTurniConfig] = useState(false);
  const turnoOrari = turniConfig.orari || DEFAULT_TURNI_CONFIG.orari;
  const pomeriggioCortoConfig = turniConfig.pomeriggio_corto || DEFAULT_TURNI_CONFIG.pomeriggio_corto;
  const schemaOrarioAttivo = turniConfig.schema_orario || "standard";

  const applyTurniConfigState = (rawConfig) => {
    const config = mergeTurniConfig(rawConfig);
    setTurniConfig(config);
    setRegolaMaxNottiCalendario(config.max_notti_consecutive || 3);
    setRegolaRiposoNottiCalendario(config.riposo_dopo_notti ?? 1);
    return config;
  };

  const handleSchemaOrarioChange = async (schemaOrario) => {
    if (!schemaOrario || schemaOrario === schemaOrarioAttivo) return;
    setSavingTurniConfig(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/oss/turni-config`,
        { schema_orario: schemaOrario },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      applyTurniConfigState(data.config);
      toast.success(
        schemaOrario === "sei_ore"
          ? "Orari turni impostati su 6 ore (7-13 · 13-19 · 19-7)"
          : "Orari turni impostati su 7 ore (7-14 · 14-21 · 21-7)"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio orari turni");
    } finally {
      setSavingTurniConfig(false);
    }
  };
  const [ossPreferenzeModal, setOssPreferenzeModal] = useState(null);
  const [preferenzeForm, setPreferenzeForm] = useState({ mattina: "", pomeriggio: "", notte: "" });
  const [savingPreferenze, setSavingPreferenze] = useState(false);
  const [sendingOssAccessId, setSendingOssAccessId] = useState(null);

  // Verifica subscription al caricamento
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const subscriptionStatus = searchParams.get("subscription");
    
    if (subscriptionStatus === "success" && sessionId) {
      verifySubscription(sessionId);
    } else if (subscriptionStatus === "cancelled") {
      toast.error("Abbonamento annullato");
    }
  }, [searchParams]);

  // Rileva mobile al mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  // Carica dati iniziali
  useEffect(() => {
    fetchCandidati();
    fetchUserPlan();
    fetchOss();
  }, [token]);

  // Intervallo mese per calendario turni (primo - ultimo giorno)
  const getMonthRange = (firstDay) => {
    const from = firstDay;
    const d = new Date(firstDay + "T12:00:00");
    d.setMonth(d.getMonth() + 1);
    d.setDate(0);
    const to = d.toISOString().slice(0, 10);
    return { from, to };
  };

  useEffect(() => {
    if (!token || !monthStart) return;
    const { from, to } = getMonthRange(monthStart);
    const fetchTurni = async () => {
      setLoadingTurni(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [turniRes, ferieRes, configRes] = await Promise.all([
          axios.get(`${API_URL}/api/oss/turni`, {
            params: { from_date: from, to_date: to },
            headers
          }),
          axios.get(`${API_URL}/api/oss/ferie`, {
            params: { from_date: from, to_date: to },
            headers
          }),
          axios.get(`${API_URL}/api/oss/turni-config`, { headers })
        ]);
        setTurni(turniRes.data.turni || []);
        setFerie(ferieRes.data.ferie || []);
        applyTurniConfigState(configRes.data.config);
      } catch (e) {
        console.error("Errore caricamento turni/ferie:", e);
        setTurni([]);
        setFerie([]);
      } finally {
        setLoadingTurni(false);
      }
    };
    fetchTurni();
  }, [token, monthStart]);

  const refreshTurniEFerie = async () => {
    const { from, to } = getMonthRange(monthStart);
    const headers = { Authorization: `Bearer ${token}` };
    const [turniRes, ferieRes, configRes] = await Promise.all([
      axios.get(`${API_URL}/api/oss/turni`, {
        params: { from_date: from, to_date: to },
        headers
      }),
      axios.get(`${API_URL}/api/oss/ferie`, {
        params: { from_date: from, to_date: to },
        headers
      }),
      axios.get(`${API_URL}/api/oss/turni-config`, { headers })
    ]);
    setTurni(turniRes.data.turni || []);
    setFerie(ferieRes.data.ferie || []);
    applyTurniConfigState(configRes.data.config);
  };

  // Carica richieste quando il piano è attivo
  useEffect(() => {
    if (userPlan) {
      fetchRequests();
    }
  }, [userPlan]);

  const verifySubscription = async (sessionId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/recruiting/subscription/verify?session_id=${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "ACTIVE") {
        toast.success(response.data.message);
        setUserPlan(response.data.plan);
        // Rimuovi parametri URL
        window.history.replaceState({}, document.title, "/personale");
      }
    } catch (error) {
      console.error("Errore verifica subscription:", error);
    }
  };

  const fetchUserPlan = async () => {
    setLoadingPlan(true);
    try {
      const response = await axios.get(`${API_URL}/api/recruiting/my-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPlan(response.data.plan);
    } catch (error) {
      console.error("Errore caricamento piano:", error);
    } finally {
      setLoadingPlan(false);
    }
  };

  const fetchCandidati = async () => {
    setLoadingCandidati(true);
    try {
      const response = await axios.get(`${API_URL}/api/recruiting/candidati`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidati(response.data.candidati || []);
      setComuneStruttura(response.data.comune_struttura || "");
      setRaggioKm(response.data.raggio_km || 30);
    } catch (error) {
      console.error("Errore caricamento candidati:", error);
      toast.error("Errore nel caricamento candidati");
    } finally {
      setLoadingCandidati(false);
    }
  };

  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API_URL}/api/recruiting/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error("Errore caricamento richieste:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchOss = async () => {
    setLoadingOss(true);
    try {
      const response = await axios.get(`${API_URL}/api/oss`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOssList(response.data.oss || []);
    } catch (error) {
      console.error("Errore caricamento OSS:", error);
      setOssList([]);
    } finally {
      setLoadingOss(false);
    }
  };

  const openOssModalCreate = (figura = STAFF_FIGURA_OSS) => {
    setEditingOssId(null);
    const defaultSpec = staffSpecializzazioneOptions(figura)[0]?.value || "";
    setOssForm({
      nome: "",
      cognome: "",
      email: "",
      password: "",
      telefono: "",
      figura,
      specializzazione: isFiguraAttivita(figura) ? defaultSpec : "",
      specializzazione_altro: "",
      invia_email_accesso: figura === STAFF_FIGURA_OSS,
      auth_mode: "email",
      login_code: "",
      regenerate_login_code: false,
      responsabile: false,
    });
    setShowOssModal(true);
  };

  const openOssModalEdit = (oss) => {
    setEditingOssId(oss.id);
    const figura = staffFigura(oss);
    const authMode =
      oss.auth_mode === "code" || (!oss.email?.trim() && oss.login_code)
        ? "code"
        : "email";
    setOssForm({
      nome: oss.nome || "",
      cognome: oss.cognome || "",
      email: oss.email || "",
      password: "",
      telefono: oss.telefono || "",
      figura,
      specializzazione: oss.specializzazione || (isFiguraAttivita(figura) ? staffSpecializzazioneOptions(figura)[0]?.value || "" : ""),
      specializzazione_altro: oss.specializzazione_altro || "",
      invia_email_accesso: false,
      auth_mode: authMode,
      login_code: oss.login_code || "",
      regenerate_login_code: false,
      responsabile: !!oss.responsabile,
    });
    setShowOssModal(true);
  };

  const findStaffById = (id) => ossList.find((o) => String(o.id) === String(id));
  const staffIdIsOss = (id) => {
    const s = findStaffById(id);
    return !s || isOssFigura(s);
  };

  const handleSaveOss = async () => {
    if (!ossForm.nome?.trim() || !ossForm.cognome?.trim()) {
      toast.error("Compila nome e cognome");
      return;
    }
    const figura = ossForm.figura || STAFF_FIGURA_OSS;
    const isOss = isOssFigura({ figura });
    const authMode = isOss ? ossForm.auth_mode || "email" : "email";
    const allowEmptyEmail =
      (isOss && authMode === "code") || (!isOss && emailOptionalForFigura(figura));
    if (isOss && authMode === "email" && !ossForm.email?.trim()) {
      toast.error("Inserisci l'email per l'accesso");
      return;
    }
    if (!isOss && !allowEmptyEmail && !ossForm.email?.trim()) {
      toast.error("Compila nome, cognome e email");
      return;
    }
    if (!editingOssId && isOss && !ossForm.password?.trim()) {
      toast.error("Inserisci la password");
      return;
    }
    if (ossForm.password?.trim() && ossForm.password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    if (isFiguraAttivita(figura)) {
      if (!ossForm.specializzazione?.trim()) {
        toast.error("Seleziona la specializzazione");
        return;
      }
      if (ossForm.specializzazione === "altro" && !ossForm.specializzazione_altro?.trim()) {
        toast.error("Indica la specializzazione nel campo libero");
        return;
      }
    }
    setSubmittingOss(true);
    try {
      if (editingOssId) {
        const payload = {
          nome: ossForm.nome.trim(),
          cognome: ossForm.cognome.trim(),
          email: allowEmptyEmail ? (ossForm.email?.trim() || "") : ossForm.email.trim(),
          telefono: ossForm.telefono?.trim() ?? "",
          invia_email_accesso:
            isOss && authMode === "email" && !!ossForm.invia_email_accesso && !!ossForm.email?.trim(),
        };
        if (isOss) {
          payload.auth_mode = authMode;
          payload.responsabile = !!ossForm.responsabile;
          if (authMode === "code" && ossForm.regenerate_login_code) {
            payload.regenerate_login_code = true;
          }
        }
        if (isFiguraAttivita(figura)) {
          payload.specializzazione = ossForm.specializzazione;
          payload.specializzazione_altro =
            ossForm.specializzazione === "altro" ? ossForm.specializzazione_altro.trim() : "";
        }
        if (ossForm.password?.trim()) {
          payload.password = ossForm.password;
        }
        const { data } = await axios.patch(`${API_URL}/api/oss/${editingOssId}/profilo`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (payload.invia_email_accesso) {
          if (data.access_email_sent === false) {
            toast.error("Dati salvati, ma email di accesso non inviata: controlla la configurazione dell'invio.");
          } else if (data.access_email_sent === true) {
            toast.success("Dati salvati e email di accesso inviata all'operatore.");
          } else {
            toast.success("Dati aggiornati");
          }
        } else {
          toast.success("Dati aggiornati");
        }
        if (data.login_code && (ossForm.regenerate_login_code || !ossForm.login_code)) {
          setOssCodeReveal({
            nome: data.nome || ossForm.nome,
            cognome: data.cognome || ossForm.cognome,
            login_code: data.login_code,
          });
        }
      } else {
        const body = {
          nome: ossForm.nome.trim(),
          cognome: ossForm.cognome.trim(),
          email: allowEmptyEmail ? (ossForm.email?.trim() || null) : ossForm.email.trim(),
          figura,
          telefono: ossForm.telefono?.trim() ?? "",
          invia_email_accesso:
            isOss && authMode === "email" && !!ossForm.invia_email_accesso && !!ossForm.email?.trim(),
        };
        if (isOss) {
          body.auth_mode = authMode;
          body.responsabile = !!ossForm.responsabile;
        }
        if (isFiguraAttivita(figura)) {
          body.specializzazione = ossForm.specializzazione;
          if (ossForm.specializzazione === "altro") {
            body.specializzazione_altro = ossForm.specializzazione_altro.trim();
          }
        }
        if (isOss) {
          body.password = ossForm.password;
        } else if (ossForm.password?.trim()) {
          body.password = ossForm.password;
        }
        const { data } = await axios.post(`${API_URL}/api/oss`, body, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (body.invia_email_accesso) {
          if (data.access_email_sent === false) {
            toast.error("OSS creato, ma email con credenziali non inviata: controlla la configurazione dell'invio.");
          } else if (data.access_email_sent === true) {
            toast.success("OSS aggiunto. Email con link e password inviata all'operatore.");
          } else {
            toast.success("OSS aggiunto");
          }
        } else if (data.login_code) {
          toast.success("OSS aggiunto");
        } else {
          toast.success(`${staffFiguraLabel(figura)} aggiunto`);
        }
        if (data.login_code) {
          setOssCodeReveal({
            nome: data.nome || ossForm.nome,
            cognome: data.cognome || ossForm.cognome,
            login_code: data.login_code,
          });
        }
      }
      setShowOssModal(false);
      setEditingOssId(null);
      setOssForm({
        nome: "",
        cognome: "",
        email: "",
        password: "",
        telefono: "",
        figura: STAFF_FIGURA_OSS,
        specializzazione: "",
        specializzazione_altro: "",
        invia_email_accesso: true,
        auth_mode: "email",
        login_code: "",
        regenerate_login_code: false,
        responsabile: false,
      });
      fetchOss();
    } catch (error) {
      console.error(editingOssId ? "Errore aggiornamento OSS:" : "Errore creazione OSS:", error);
      toast.error(
        error.response?.data?.detail ||
          (editingOssId ? "Errore nel salvataggio" : "Errore nell'aggiunta dell'operatore")
      );
    } finally {
      setSubmittingOss(false);
    }
  };

  const handleDeleteOss = async (id) => {
    if (!window.confirm("Rimuovere questo OSS dall'elenco?")) return;
    try {
      await axios.delete(`${API_URL}/api/oss/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("OSS rimosso");
      fetchOss();
    } catch (error) {
      console.error("Errore eliminazione OSS:", error);
      toast.error(error.response?.data?.detail || "Errore nella rimozione");
    }
  };

  const ossLoginPageUrl =
    typeof window !== "undefined" ? `${window.location.origin}/oss-login` : "/oss-login";

  const handleInviaEmailAccessoOss = async (oss) => {
    if (!oss?.email?.trim()) {
      toast.error("L'OSS non ha un indirizzo email");
      return;
    }
    setSendingOssAccessId(oss.id);
    try {
      await axios.post(
        `${API_URL}/api/oss/${oss.id}/invia-email-accesso`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email con link e istruzioni di accesso inviata");
    } catch (error) {
      console.error("Errore invio email accesso OSS:", error);
      const d = error.response?.data?.detail;
      const msg =
        typeof d === "string"
          ? d
          : Array.isArray(d)
            ? d.map((x) => x?.msg || x).join(" ")
            : "Errore nell'invio dell'email";
      toast.error(msg);
    } finally {
      setSendingOssAccessId(null);
    }
  };

  const handleSavePreferenze = async () => {
    if (!ossPreferenzeModal) return;
    setSavingPreferenze(true);
    try {
      const payload = {
        preferenze_turni: {
          mattina: preferenzeForm.mattina || null,
          pomeriggio: preferenzeForm.pomeriggio || null,
          notte: preferenzeForm.notte || null
        }
      };
      await axios.patch(`${API_URL}/api/oss/${ossPreferenzeModal.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Preferenze aggiornate");
      setOssPreferenzeModal(null);
      fetchOss();
    } catch (error) {
      console.error("Errore aggiornamento preferenze:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingPreferenze(false);
    }
  };

  const handleAddTurno = (data, turno) => {
    setTurnoModalData({ data, turno });
    setSelectedOssIdsForTurno([]);
    setShowTurnoModal(true);
  };

  const openFerieModal = ({ ossId = "", date = "" } = {}) => {
    setFerieForm({
      oss_id: ossId,
      from_date: date,
      to_date: date,
      note: ""
    });
    setShowFerieModal(true);
  };

  const handleSaveFerie = async () => {
    if (!ferieForm.oss_id || !ferieForm.from_date || !ferieForm.to_date) {
      toast.error("Seleziona operatore e intervallo ferie");
      return;
    }
    if (ferieForm.to_date < ferieForm.from_date) {
      toast.error("La data fine non può precedere la data inizio");
      return;
    }
    setSavingFerie(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/oss/ferie`,
        ferieForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshTurniEFerie();
      const removed = data?.turni_removed || 0;
      toast.success(removed > 0 ? `Ferie inserite. Rimossi ${removed} turni in conflitto.` : "Ferie inserite.");
      setShowFerieModal(false);
      setFerieForm({ oss_id: "", from_date: "", to_date: "", note: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'inserimento ferie");
    } finally {
      setSavingFerie(false);
    }
  };

  const handleDeleteFerie = async (ferieId) => {
    if (!window.confirm("Rimuovere questo periodo di ferie?")) return;
    try {
      await axios.delete(`${API_URL}/api/oss/ferie/${ferieId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshTurniEFerie();
      toast.success("Ferie rimosse");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella rimozione ferie");
    }
  };

  const toggleOssForTurno = (ossId) => {
    setSelectedOssIdsForTurno((prev) =>
      prev.includes(ossId) ? prev.filter((id) => id !== ossId) : [...prev, ossId]
    );
  };

  const handleSaveTurno = async (ossIds) => {
    const ids = Array.isArray(ossIds) ? ossIds : (ossIds ? [ossIds] : []);
    if (!turnoModalData.data || !turnoModalData.turno || ids.length === 0) return;
    const alreadyAssignedMsg = "Questo turno è già assegnato per questa data";
    const range = turnoOrari[turnoModalData.turno];
    const results = await Promise.allSettled(
      ids.map((ossId) =>
        axios.post(
          `${API_URL}/api/oss/turni`,
          {
            oss_id: ossId,
            data: turnoModalData.data,
            turno: turnoModalData.turno,
            ora_inizio: range?.inizio,
            ora_fine: range?.fine,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      )
    );
    const otherError = results.find(
      (r) => r.status === "rejected" && r.reason?.response?.data?.detail !== alreadyAssignedMsg
    );
    if (otherError) {
      toast.error(otherError.reason?.response?.data?.detail || "Errore nell'assegnazione");
      return;
    }
    setPendingTurnoChanges((prev) => {
      const next = { ...prev };
      const data = turnoModalData.data;
      const turno = turnoModalData.turno;
      ids.forEach((ossId) => {
        const key = String(ossId);
        if (!next[key]) next[key] = [];
        next[key].push({
          type: "assegnato",
          data,
          turno,
          ora_inizio: range?.inizio,
          ora_fine: range?.fine,
        });
      });
      return next;
    });
    toast.success(ids.length === 1 ? "Turno assegnato." : `${ids.length} turni assegnati.`);
    setShowTurnoModal(false);
    setTurnoModalData({ data: null, turno: null });
    setSelectedOssIdsForTurno([]);
    const { from, to } = getMonthRange(monthStart);
    const res = await axios.get(`${API_URL}/api/oss/turni`, {
      params: { from_date: from, to_date: to },
      headers: { Authorization: `Bearer ${token}` }
    });
    setTurni(res.data.turni || []);
  };

  const pushTurniRimosseToPending = (docs) => {
    if (!docs?.length) return;
    setPendingTurnoChanges((prev) => {
      const next = { ...prev };
      docs.forEach((turnoToRemove) => {
        if (!turnoToRemove?.oss_id) return;
        const key = String(turnoToRemove.oss_id);
        if (!next[key]) next[key] = [];
        next[key].push({
          type: "rimosso",
          data: turnoToRemove.data,
          turno: turnoToRemove.turno,
          tipo_giorno: turnoToRemove.tipo_giorno,
          ora_inizio: turnoToRemove.ora_inizio,
          ora_fine: turnoToRemove.ora_fine,
        });
      });
      return next;
    });
  };

  const deleteTurniViaApiIds = async (docs) => {
    if (!docs?.length || !token) return { succeeded: [], failedCount: docs.length };
    const results = await Promise.allSettled(
      docs.map((t) =>
        axios.delete(`${API_URL}/api/oss/turni/${t.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      )
    );
    const succeeded = docs.filter((_, i) => results[i].status === "fulfilled");
    return { succeeded, failedCount: docs.length - succeeded.length };
  };

  const handleDeleteTurno = async (turnoId) => {
    const turnoToRemove = turni.find((t) => t.id === turnoId);
    if (!window.confirm("Rimuovere questo turno?")) return;
    try {
      await axios.delete(`${API_URL}/api/oss/turni/${turnoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (turnoToRemove?.oss_id) pushTurniRimosseToPending([turnoToRemove]);
      toast.success("Turno rimosso.");
      const { from, to } = getMonthRange(monthStart);
      const res = await axios.get(`${API_URL}/api/oss/turni`, {
        params: { from_date: from, to_date: to },
        headers: { Authorization: `Bearer ${token}` }
      });
      setTurni(res.data.turni || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella rimozione");
    }
  };

  const clearingTurniBusy = clearingMonthTurni || clearingRowOssId != null;

  const capitalizeItLabel = (s) =>
    (s && `${s.charAt(0).toLocaleUpperCase("it-IT")}${s.slice(1)}`) || "";

  const openBulkClearMonthDialog = () => {
    if (!token || clearingTurniBusy) return;
    const count = turni.length;
    if (count === 0) {
      toast.info("Non ci sono turni da rimuovere per questo mese.");
      return;
    }
    const d = new Date(monthStart + "T12:00:00");
    const raw = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    setBulkClearDialog({
      type: "month",
      count,
      monthLabelPretty: capitalizeItLabel(raw)
    });
  };

  const openBulkClearRowDialog = (ossId, nominativo) => {
    if (!token || clearingTurniBusy) return;
    const rowTurni = turni.filter((t) => String(t.oss_id) === String(ossId));
    if (rowTurni.length === 0) {
      toast.info(`${nominativo}: nessun turno da rimuovere in questo mese.`);
      return;
    }
    const d = new Date(monthStart + "T12:00:00");
    const raw = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    setBulkClearDialog({
      type: "row",
      ossId,
      nominativo,
      count: rowTurni.length,
      monthLabelPretty: capitalizeItLabel(raw)
    });
  };

  const confirmBulkClearDialog = async () => {
    const spec = bulkClearDialog;
    if (!spec || !token || clearingTurniBusy) return;
    setBulkClearDialog(null);
    if (spec.type === "month") {
      setClearingMonthTurni(true);
      try {
        const snapshot = [...turni];
        const { succeeded, failedCount } = await deleteTurniViaApiIds(snapshot);
        if (succeeded.length) pushTurniRimosseToPending(succeeded);
        await refreshTurniEFerie();
        if (failedCount > 0) {
          toast.error(
            `Rimossi ${succeeded.length} turni su ${snapshot.length}. ` +
              (failedCount === snapshot.length ? "Operazione fallita." : "Alcune eliminazioni non sono riuscite.")
          );
        } else {
          toast.success(`${succeeded.length === 1 ? "Rimosso 1 turno" : `Rimossi ${succeeded.length} turni`} nel mese.`);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore durante la rimozione dei turni.");
      } finally {
        setClearingMonthTurni(false);
      }
      return;
    }
    const rowTurni = turni.filter((t) => String(t.oss_id) === String(spec.ossId));
    setClearingRowOssId(String(spec.ossId));
    try {
      const { succeeded, failedCount } = await deleteTurniViaApiIds(rowTurni);
      if (succeeded.length) pushTurniRimosseToPending(succeeded);
      await refreshTurniEFerie();
      if (failedCount > 0) {
        toast.error(`Rimosse solo ${succeeded.length} di ${rowTurni.length} assegnazioni. Riprovare.`);
      } else {
        toast.success(`${spec.nominativo}: riga svuotata per questo mese.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la rimozione dei turni.");
    } finally {
      setClearingRowOssId(null);
    }
  };

  // Helper: formatta Date in YYYY-MM-DD secondo il fuso locale (evita spostamenti di giorno dovuti a UTC)
  const formatDateLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Helper: lettera (IT) del giorno della settimana
  const getDayLetterIt = (date) => {
    const letters = ["D", "L", "M", "M", "G", "V", "S"];
    return letters[date.getDay()];
  };

  const getFerieForOssDate = (ossId, dateStr) =>
    ferie.find(
      (f) =>
        String(f.oss_id) === String(ossId) &&
        f.from_date <= dateStr &&
        f.to_date >= dateStr
    ) || null;

  const addDaysToDateStr = (dateStr, days) => {
    const d = new Date(dateStr + "T12:00:00");
    d.setDate(d.getDate() + days);
    return formatDateLocal(d);
  };

  const getRiposoInfoForOssDate = (ossId, dateStr) => {
    const maxNotti = Math.max(1, parseInt(regolaMaxNottiCalendario, 10) || 1);
    const riposoDays = Math.max(0, parseInt(regolaRiposoNottiCalendario, 10) || 0);
    if (riposoDays <= 0) return null;

    const nightDates = turni
      .filter((t) => String(t.oss_id) === String(ossId) && t.turno === "notte")
      .map((t) => t.data)
      .filter(Boolean)
      .sort();

    let consecutive = 0;
    let previous = null;
    const restByDate = {};

    nightDates.forEach((nightDate) => {
      consecutive = previous && nightDate === addDaysToDateStr(previous, 1) ? consecutive + 1 : 1;
      if (consecutive >= maxNotti) {
        for (let i = 1; i <= riposoDays; i++) {
          const restDate = addDaysToDateStr(nightDate, i);
          restByDate[restDate] = i === 1
            ? { code: "SN", type: "smonto_notte", bg: "bg-indigo-200", text: "text-indigo-900", label: "Smonto notte" }
            : { code: "R", type: "riposo_notte", bg: "bg-violet-200", text: "text-violet-900", label: "Riposo dopo notti" };
        }
        consecutive = 0;
      }
      previous = nightDate;
    });

    return restByDate[dateStr] || null;
  };

  const labelTurnoConOrario = (label, range) => {
    const formatted = formatTurnoRange(range);
    return formatted ? `${label} (${formatted})` : label;
  };

  const getTurnoRangeFromRecord = (turnoRecord, fallbackKey) => {
    if (turnoRecord?.ora_inizio && turnoRecord?.ora_fine) {
      return { inizio: turnoRecord.ora_inizio, fine: turnoRecord.ora_fine };
    }
    return turnoOrari[fallbackKey];
  };

  const isPomeriggioCorto = (turnoRecord) => {
    if (!turnoRecord || turnoRecord.turno !== "pomeriggio") return false;
    if (turnoRecord.variante === "pomeriggio_corto") return true;
    return Boolean(
      turnoRecord.ora_inizio &&
        turnoRecord.ora_fine &&
        (turnoRecord.ora_inizio !== turnoOrari.pomeriggio?.inizio ||
          turnoRecord.ora_fine !== turnoOrari.pomeriggio?.fine)
    );
  };

  // Deriva il "codice turno" di un OSS in un certo giorno: M / P / N / M-P / AO / S / R
  const getShiftInfoForOss = (ossId, dateStr) => {
    if (getFerieForOssDate(ossId, dateStr)) {
      return { code: "F", type: "ferie", bg: "bg-amber-200", text: "text-amber-900", label: "Ferie" };
    }
    const dayTurni = turni.filter(
      (t) => String(t.oss_id) === String(ossId) && t.data === dateStr
    );
    if (dayTurni.length === 0) {
      return staffIdIsOss(ossId) ? getRiposoInfoForOssDate(ossId, dateStr) : null;
    }
    const hasSmontante = dayTurni.some((t) => t.tipo_giorno === "smontante" || t.turno === "smontante");
    const hasRiposo = dayTurni.some((t) => t.tipo_giorno === "riposo" || t.turno === "riposo");
    if (hasSmontante) {
      return { code: "S", type: "smontante", bg: "bg-indigo-200", text: "text-indigo-900", label: "Smontante" };
    }
    if (hasRiposo) {
      return { code: "R", type: "riposo", bg: "bg-violet-200", text: "text-violet-900", label: "Riposo" };
    }
    const hasAttivita = dayTurni.some((t) => t.turno === TURNO_ATTIVITA_OCCUPAZIONALE);
    if (hasAttivita) {
      return {
        code: "AO",
        type: TURNO_ATTIVITA_OCCUPAZIONALE,
        bg: "bg-lime-200",
        text: "text-lime-900",
        label: "Attività occupazionale",
      };
    }
    const hasMattina = dayTurni.some((t) => t.turno === "mattina");
    const hasPomeriggio = dayTurni.some((t) => t.turno === "pomeriggio");
    const hasNotte = dayTurni.some((t) => t.turno === "notte");
    const pomeriggioRecord = dayTurni.find((t) => t.turno === "pomeriggio");
    const mattinaRecord = dayTurni.find((t) => t.turno === "mattina");
    const notteRecord = dayTurni.find((t) => t.turno === "notte");
    if (hasMattina && hasPomeriggio) {
      return {
        code: "M-P",
        type: "lunga",
        bg: "bg-emerald-300",
        text: "text-emerald-900",
        label: labelTurnoConOrario("Lunga", turnoOrari.lunga),
      };
    }
    if (hasMattina) {
      return {
        code: "M",
        type: "mattina",
        bg: "bg-sky-200",
        text: "text-sky-900",
        label: labelTurnoConOrario("Mattina", getTurnoRangeFromRecord(mattinaRecord, "mattina")),
      };
    }
    if (hasPomeriggio) {
      const corto = isPomeriggioCorto(pomeriggioRecord);
      return {
        code: corto ? "P*" : "P",
        type: "pomeriggio",
        bg: corto ? "bg-fuchsia-200" : "bg-rose-200",
        text: corto ? "text-fuchsia-900" : "text-rose-900",
        label: labelTurnoConOrario(corto ? "Pomeriggio corto" : "Pomeriggio", getTurnoRangeFromRecord(pomeriggioRecord, "pomeriggio")),
      };
    }
    if (hasNotte) {
      return {
        code: "N",
        type: "notte",
        bg: "bg-slate-300",
        text: "text-slate-800",
        label: labelTurnoConOrario("Notte", getTurnoRangeFromRecord(notteRecord, "notte")),
      };
    }
    return null;
  };

  const handleCellTurnoClick = (ossId, dateStr) => {
    setCellTurnoData({ ossId, data: dateStr });
    setShowCellTurnoModal(true);
  };

  // Imposta (o azzera) il turno di un OSS in un certo giorno.
  // newType ∈ { null, "mattina", "pomeriggio", "notte", "lunga", "attivita_occupazionale" }
  const handleSetCellTurno = async (newType) => {
    const { ossId, data } = cellTurnoData;
    if (!ossId || !data) return;
    if (getFerieForOssDate(ossId, data)) {
      toast.error("L'operatore è in ferie in questa data: rimuovi prima le ferie per assegnare un turno.");
      return;
    }
    const existing = turni.filter(
      (t) => String(t.oss_id) === String(ossId) && t.data === data
    );
    let desired = [];
    if (newType === "mattina") desired = ["mattina"];
    else if (newType === "pomeriggio") desired = ["pomeriggio"];
    else if (newType === "notte") desired = ["notte"];
    else if (newType === "lunga") desired = ["mattina", "pomeriggio"];
    else if (newType === TURNO_ATTIVITA_OCCUPAZIONALE) desired = [TURNO_ATTIVITA_OCCUPAZIONALE];
    const existingTypes = existing.map((e) => e.turno);
    const toDelete = existing.filter((e) => !desired.includes(e.turno));
    const toAdd = desired.filter((t) => !existingTypes.includes(t));
    if (toDelete.length === 0 && toAdd.length === 0) {
      setShowCellTurnoModal(false);
      return;
    }
    setSavingCellTurno(true);
    try {
      for (const t of toDelete) {
        await axios.delete(`${API_URL}/api/oss/turni/${t.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      for (const tType of toAdd) {
        const range = tType === TURNO_ATTIVITA_OCCUPAZIONALE ? turnoOrari.lunga : turnoOrari[tType];
        await axios.post(
          `${API_URL}/api/oss/turni`,
          {
            oss_id: ossId,
            data,
            turno: tType,
            ora_inizio: range?.inizio,
            ora_fine: range?.fine,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setPendingTurnoChanges((prev) => {
        const next = { ...prev };
        const key = String(ossId);
        if (!next[key]) next[key] = [];
        else next[key] = [...next[key]];
        toDelete.forEach((t) => next[key].push({
          type: "rimosso",
          data,
          turno: t.turno,
          tipo_giorno: t.tipo_giorno,
          ora_inizio: t.ora_inizio,
          ora_fine: t.ora_fine,
        }));
        toAdd.forEach((tType) => next[key].push({ type: "assegnato", data, turno: tType }));
        return next;
      });
      const { from, to } = getMonthRange(monthStart);
      const res = await axios.get(`${API_URL}/api/oss/turni`, {
        params: { from_date: from, to_date: to },
        headers: { Authorization: `Bearer ${token}` }
      });
      setTurni(res.data.turni || []);
      toast.success("Turno aggiornato");
      setShowCellTurnoModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento turno");
    } finally {
      setSavingCellTurno(false);
    }
  };

  const handleSendTurnoSummary = async () => {
    const entries = Object.entries(pendingTurnoChanges).filter(([, arr]) => arr?.length > 0);
    if (entries.length === 0) return;
    setSendingTurnoSummary(true);
    try {
      const changes = Object.fromEntries(
        entries.map(([ossId, arr]) => [
          ossId,
          arr.map((c) => ({
            type: c.type,
            data: c.data,
            turno: c.turno,
            tipo_giorno: c.tipo_giorno,
            ora_inizio: c.ora_inizio,
            ora_fine: c.ora_fine,
          })),
        ])
      );
      await axios.post(`${API_URL}/api/oss/turni/notify-changes`, { changes }, { headers: { Authorization: `Bearer ${token}` } });
      setPendingTurnoChanges({});
      toast.success("Email riepilogo inviate agli OSS.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio delle email");
    } finally {
      setSendingTurnoSummary(false);
    }
  };

  const handleEmailTurniMese = async () => {
    const monthParam = (monthStart || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(monthParam)) {
      toast.error("Seleziona un mese valido");
      return;
    }
    setSendingTurniMeseEmail(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/oss/turni/email-mese`,
        { month: monthParam },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data?.message || "Richiesta completata.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio delle email con i turni del mese");
    } finally {
      setSendingTurniMeseEmail(false);
    }
  };

  const buildGeneraTurniRequestBody = (monthParam) => {
    const body = {
      month: monthParam,
      oss_ids: generaOssIds,
      sostituisci: generaSostituisci,
      usa_esistenti_come_base: !generaSostituisci && generaUsaEsistentiBase,
      modalita: generaModalita,
      usa_ai: generaUsaIstruzioni && !!generaIstruzioniNote.trim(),
      ai_note: generaIstruzioniNote.trim() || null,
    };
    if (generaModalita === "schema_ppmmnnsr") {
      body.oss_ids_assenti_domenica = generaOssIdsAssentiDomenica.filter((id) => generaOssIds.includes(id));
    } else if (generaModalita === "schema_nnsrlp" || generaModalita === "schema_nnlpsr") {
      body.oss_ids_assenti_domenica = generaOssIdsAssentiDomenica.filter((id) => generaOssIds.includes(id));
    } else {
      body.max_notti_consecutive = generaMaxNotti;
      body.riposo_dopo_notti = generaRiposo;
      body.turni_mattina_per_giorno = generaTurniMattina;
      body.turni_pomeriggio_per_giorno = generaTurniPomeriggio;
      body.turni_notte_per_giorno = generaTurniNotte;
      body.turni_lunghe_per_giorno = generaTurniLunghe;
      body.riposo_dopo_lunga = generaRiposoDopoLunga;
      body.oss_ids_lunghe = generaOssIdsLunghe.filter((id) => generaOssIds.includes(id));
      body.oss_ids_assenti_domenica = generaOssIdsAssentiDomenica.filter((id) => generaOssIds.includes(id));
      body.oss_ids_solo_mattina = generaOssIdsSoloMattina.filter((id) => generaOssIds.includes(id));
      body.oss_ids_solo_pomeriggio = generaOssIdsSoloPomeriggio.filter((id) => generaOssIds.includes(id));
      body.oss_ids_solo_notte = generaOssIdsSoloNotte.filter((id) => generaOssIds.includes(id));
      if (generaCoperturaFestivi) {
        body.turni_mattina_per_giorno_festivo = generaTurniMattinaFestivo;
        body.turni_pomeriggio_per_giorno_festivo = generaTurniPomeriggioFestivo;
        body.turni_notte_per_giorno_festivo = generaTurniNotteFestivo;
        body.turni_lunghe_per_giorno_festivo = generaTurniLungheFestivo;
      }
    }
    return body;
  };

  const loadTurniTemplates = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/oss/turni-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = data.items || [];
      setTurniTemplates(items);
      const defaultTemplate = items.find((item) => item.predefinito);
      if (defaultTemplate && !selectedTurniTemplateId) {
        setSelectedTurniTemplateId(defaultTemplate.id);
      }
    } catch {
      setTurniTemplates([]);
    }
  }, [token, selectedTurniTemplateId]);

  const applyTurniTemplate = (templateId) => {
    setSelectedTurniTemplateId(templateId);
    const params = turniTemplates.find((item) => item.id === templateId)?.parametri;
    if (!params) return;
    setGeneraModalita(params.modalita || "copertura");
    setGeneraSostituisci(params.sostituisci !== false);
    setGeneraUsaEsistentiBase(Boolean(params.usa_esistenti_come_base));
    setGeneraMaxNotti(params.max_notti_consecutive ?? 3);
    setGeneraRiposo(params.riposo_dopo_notti ?? 1);
    setGeneraTurniMattina(params.turni_mattina_per_giorno ?? 1);
    setGeneraTurniPomeriggio(params.turni_pomeriggio_per_giorno ?? 1);
    setGeneraTurniNotte(params.turni_notte_per_giorno ?? 1);
    setGeneraTurniLunghe(params.turni_lunghe_per_giorno ?? 0);
    setGeneraRiposoDopoLunga(params.riposo_dopo_lunga ?? 1);
    setGeneraOssIds(params.oss_ids || ossOnlyList.map((oss) => oss.id));
    setGeneraOssIdsLunghe(params.oss_ids_lunghe || []);
    setGeneraOssIdsAssentiDomenica(params.oss_ids_assenti_domenica || []);
    setGeneraOssIdsSoloMattina(params.oss_ids_solo_mattina || []);
    setGeneraOssIdsSoloPomeriggio(params.oss_ids_solo_pomeriggio || []);
    setGeneraOssIdsSoloNotte(params.oss_ids_solo_notte || []);
    setGeneraCoperturaFestivi(params.turni_mattina_per_giorno_festivo != null);
    setGeneraTurniMattinaFestivo(params.turni_mattina_per_giorno_festivo ?? 1);
    setGeneraTurniPomeriggioFestivo(params.turni_pomeriggio_per_giorno_festivo ?? 1);
    setGeneraTurniNotteFestivo(params.turni_notte_per_giorno_festivo ?? 1);
    setGeneraTurniLungheFestivo(params.turni_lunghe_per_giorno_festivo ?? 0);
    setGeneraUsaIstruzioni(Boolean(params.usa_ai));
    setGeneraIstruzioniNote(params.ai_note || "");
  };

  const saveTurniTemplate = async () => {
    const nome = turniTemplateName.trim();
    if (!nome) {
      toast.error("Inserisci un nome per il template");
      return;
    }
    const { month, ...parametri } = buildGeneraTurniRequestBody((generaMonth || monthStart).slice(0, 7));
    setSavingTurniTemplate(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/oss/turni-template`,
        { nome, parametri, predefinito: !turniTemplates.some((item) => item.predefinito) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await loadTurniTemplates();
      setSelectedTurniTemplateId(data.id);
      setTurniTemplateName("");
      toast.success("Template turni salvato");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile salvare il template");
    } finally {
      setSavingTurniTemplate(false);
    }
  };

  const setTurniTemplateDefault = async () => {
    const template = turniTemplates.find((item) => item.id === selectedTurniTemplateId);
    if (!template) return;
    try {
      await axios.put(
        `${API_URL}/api/oss/turni-template/${template.id}`,
        { nome: template.nome, parametri: template.parametri, predefinito: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await loadTurniTemplates();
      toast.success("Template predefinito aggiornato");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile impostare il template predefinito");
    }
  };

  const openTurniAutoDialog = (mode) => {
    setTurniAutoDialogMode(mode);
    setGeneraMonth(monthStart);
    const allIds = ossOnlyList.map((o) => o.id);
    setGeneraOssIds(allIds);
    setGeneraOssIdsLunghe([...allIds]);
    setGeneraOssIdsSoloMattina([]);
    setGeneraOssIdsSoloPomeriggio([]);
    setGeneraOssIdsSoloNotte([]);
    setGeneraUsaEsistentiBase(false);
    loadTurniTemplates();
    setShowGeneraTurniModal(true);
  };

  const handleGeneraTurni = async () => {
    const monthRaw = generaMonth || monthStart;
    const monthParam = monthRaw.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(monthParam)) {
      toast.error("Seleziona un mese valido (YYYY-MM)");
      return;
    }
    if (generaOssIds.length === 0) {
      toast.error("Seleziona almeno un OSS");
      return;
    }
    const usesGenerazioneAvanzata = generaUsaIstruzioni && !!generaIstruzioniNote.trim();
    setLoadingGenera(true);
    if (usesGenerazioneAvanzata) {
      setShowGeneraTurniModal(false);
      setShowGeneraAttesaModal(true);
    }
    try {
      const body = buildGeneraTurniRequestBody(monthParam);
      if (generaModalita === "schema_ppmmnnsr") {
        setRegolaMaxNottiCalendario(2);
        setRegolaRiposoNottiCalendario(2);
      } else if (generaModalita === "copertura") {
        setRegolaMaxNottiCalendario(generaMaxNotti);
        setRegolaRiposoNottiCalendario(generaRiposo);
      }
      const res = await axios.post(`${API_URL}/api/oss/turni/genera`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowGeneraTurniModal(false);
      const { from, to } = getMonthRange(monthStart);
      const [turniRes, ferieRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/oss/turni`, {
          params: { from_date: from, to_date: to },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/oss/ferie`, {
          params: { from_date: from, to_date: to },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/oss/turni-config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const newTurni = turniRes.data.turni || [];
      setTurni(newTurni);
      setFerie(ferieRes.data.ferie || []);
      applyTurniConfigState(configRes.data.config);
      // Riepilogo modifiche per invio email (come per inserimento manuale)
      const changes = {};
      newTurni.forEach((t) => {
        const key = String(t.oss_id);
        if (!changes[key]) changes[key] = [];
        changes[key].push({
          type: "assegnato",
          data: t.data,
          turno: t.turno,
          tipo_giorno: t.tipo_giorno,
          ora_inizio: t.ora_inizio,
          ora_fine: t.ora_fine,
        });
      });
      setPendingTurnoChanges(changes);
      const msg = res.data.warnings?.length
        ? `${res.data.message} (${res.data.warnings.join("; ")})`
        : res.data.message;
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella generazione turni");
    } finally {
      setLoadingGenera(false);
      setShowGeneraAttesaModal(false);
    }
  };

  const handleControllaTurni = async () => {
    const monthRaw = generaMonth || monthStart;
    const monthParam = monthRaw.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(monthParam)) {
      toast.error("Seleziona un mese valido (YYYY-MM)");
      return;
    }
    if (generaOssIds.length === 0) {
      toast.error("Seleziona almeno un OSS");
      return;
    }
    setLoadingControlla(true);
    try {
      const body = buildGeneraTurniRequestBody(monthParam);
      const res = await axios.post(`${API_URL}/api/oss/turni/controlla`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowGeneraTurniModal(false);
      setControllaRisultati(res.data);
      setShowControllaRisultatiModal(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel controllo turni");
    } finally {
      setLoadingControlla(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowUpgradeModal(true);
  };

  const handleActivatePlan = async () => {
    if (!selectedPlan) return;
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/recruiting/subscribe`,
        { plan: selectedPlan.toLowerCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.checkoutUrl) {
        // Redirect a Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } else if (response.data.success && (response.data.demo || !response.data.checkoutUrl)) {
        // Demo mode: piano attivato senza redirect
        toast.success("Piano attivato (modalità demo)");
        fetchUserPlan();
      }
    } catch (error) {
      console.error("Errore attivazione piano:", error);
      const msg = error.response?.data?.detail ?? error.message ?? "Errore nell'attivazione del piano";
      const detail = typeof msg === "string" ? msg : (msg.msg || msg.message || "Errore nell'attivazione del piano");
      toast.error(detail);
    } finally {
      setSubscribing(false);
      setShowUpgradeModal(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.ruolo.trim()) {
      toast.error("Inserisci il ruolo richiesto");
      return;
    }
    
    setSubmittingRequest(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/recruiting/requests`,
        newRequest,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("Richiesta inviata con successo!");
        setShowNewRequestModal(false);
        setNewRequest({ ruolo: "", descrizione: "", urgenza: "normale", orario: "", data_inizio: "" });
        fetchRequests();
      }
    } catch (error) {
      console.error("Errore invio richiesta:", error);
      toast.error(error.response?.data?.detail || "Errore nell'invio della richiesta");
    } finally {
      setSubmittingRequest(false);
    }
  };

  const openCandidatoDetails = (candidato) => {
    setSelectedCandidato(candidato);
  };

  const handlePrintCalendarioTurni = () => {
    const first = new Date(monthStart + "T12:00:00");
    const year = first.getFullYear();
    const month = first.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const daysInMonth = [];
    for (let i = 1; i <= lastDay; i++) {
      const d = new Date(year, month, i);
      daysInMonth.push({
        dateStr: formatDateLocal(d),
        dayNum: i,
        dayLetter: getDayLetterIt(d),
        isSunday: d.getDay() === 0,
        isSaturday: d.getDay() === 6
      });
    }

    const title = `Calendario turni – ${first.toLocaleDateString("it-IT", { month: "long", year: "numeric" }).toUpperCase()}`;

    const letterRow =
      '<th rowspan="2" class="oss-col">Personale</th>' +
      daysInMonth
        .map(
          (d) =>
            `<th class="${d.isSunday ? "sun" : d.isSaturday ? "sat" : ""}">${d.dayLetter}</th>`
        )
        .join("");
    const numRow = daysInMonth
      .map(
        (d) =>
          `<th class="${d.isSunday ? "sun" : d.isSaturday ? "sat" : ""}">${d.dayNum}</th>`
      )
      .join("");

    const getCellForPrint = (ossId, dateStr) => {
      const ferieInfo = getFerieForOssDate(ossId, dateStr);
      if (ferieInfo) return { code: "F", cls: "c-f" };
      const dayTurni = turni.filter(
        (t) => String(t.oss_id) === String(ossId) && t.data === dateStr
      );
      if (dayTurni.length === 0) {
        const riposoInfo = staffIdIsOss(ossId) ? getRiposoInfoForOssDate(ossId, dateStr) : null;
        if (riposoInfo?.type === "smonto_notte") return { code: "SN", cls: "c-sn" };
        if (riposoInfo?.type === "riposo_notte") return { code: "R", cls: "c-r" };
        return { code: "", cls: "" };
      }
      if (dayTurni.some((t) => t.tipo_giorno === "smontante" || t.turno === "smontante")) {
        return { code: "S", cls: "c-sn" };
      }
      if (dayTurni.some((t) => t.tipo_giorno === "riposo" || t.turno === "riposo")) {
        return { code: "R", cls: "c-r" };
      }
      if (dayTurni.some((t) => t.turno === TURNO_ATTIVITA_OCCUPAZIONALE)) {
        return { code: "AO", cls: "c-ao" };
      }
      const hasMattina = dayTurni.some((t) => t.turno === "mattina");
      const hasPomeriggio = dayTurni.some((t) => t.turno === "pomeriggio");
      const hasNotte = dayTurni.some((t) => t.turno === "notte");
      const pomeriggioRecord = dayTurni.find((t) => t.turno === "pomeriggio");
      if (hasMattina && hasPomeriggio) return { code: "M-P", cls: "c-mp" };
      if (hasMattina) return { code: "M", cls: "c-m" };
      if (hasPomeriggio) return { code: isPomeriggioCorto(pomeriggioRecord) ? "P*" : "P", cls: isPomeriggioCorto(pomeriggioRecord) ? "c-pc" : "c-p" };
      if (hasNotte) return { code: "N", cls: "c-n" };
      return { code: "", cls: "" };
    };

    const bodyRows = calendarioTurniSections
      .map((section, sectionIdx) => {
        const sep =
          sectionIdx > 0
            ? `<tr><td colspan="${daysInMonth.length + 1}" style="padding:0;height:6px;border-top:2px dashed #94a3b8;background:#f8fafc"></td></tr>`
            : "";
        const rows = section.rows
          .map((oss) => {
            const cells = daysInMonth
              .map((d) => {
                const info = getCellForPrint(oss.id, d.dateStr);
                const dayCls = d.isSunday ? " sun-bg" : d.isSaturday ? " sat-bg" : "";
                return `<td class="${info.cls}${dayCls}">${info.code}</td>`;
              })
              .join("");
            const rowTheme = getStaffFiguraTheme(staffFigura(oss));
            const ossName = `<span class="cognome">${(oss.cognome || "").toUpperCase()}</span> <span class="nome">${oss.nome || ""}</span>`;
            return `<tr><td class="oss-col" style="border-left:2px solid ${rowTheme.borderColor}">${ossName}</td>${cells}</tr>`;
          })
          .join("");
        return sep + rows;
      })
      .join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<style>
@page { size: A4 landscape; margin: 10mm; }
body { font-family: system-ui, sans-serif; padding: 8px; color: #0f172a; }
h1 { font-size: 16px; margin: 0 0 10px 0; text-align: center; }
table { border-collapse: collapse; width: 100%; font-size: 10px; table-layout: fixed; }
th, td { border: 1px solid #94a3b8; padding: 3px 1px; text-align: center; height: 20px; }
th { background: #f1f5f9; font-weight: 600; }
th.oss-col, td.oss-col { width: 90px; text-align: left; padding: 3px 6px; font-size: 10px; }
td.oss-col .cognome { font-weight: 700; }
td.oss-col .nome { color: #64748b; font-size: 9px; }
th.sun, td.sun { background: #fecaca; color: #991b1b; }
th.sat { background: #e2e8f0; }
td.sun-bg { background: #fee2e2; }
td.sat-bg { background: #f1f5f9; }
td.c-m { background: #bae6fd; color: #0c4a6e; font-weight: 700; }
td.c-p { background: #fecdd3; color: #881337; font-weight: 700; }
td.c-pc { background: #f5d0fe; color: #701a75; font-weight: 700; font-size: 9px; }
td.c-n { background: #cbd5e1; color: #1e293b; font-weight: 700; }
td.c-mp { background: #6ee7b7; color: #064e3b; font-weight: 700; font-size: 9px; }
td.c-ao { background: #d9f99d; color: #365314; font-weight: 700; font-size: 9px; }
td.c-f { background: #fde68a; color: #78350f; font-weight: 700; }
td.c-sn { background: #c7d2fe; color: #312e81; font-weight: 700; font-size: 9px; }
td.c-r { background: #ddd6fe; color: #4c1d95; font-weight: 700; }
.legend { margin-top: 12px; font-size: 10px; color: #475569; display: flex; gap: 12px; flex-wrap: wrap; }
.legend span { display: inline-flex; align-items: center; gap: 4px; }
.sw { display: inline-block; width: 14px; height: 14px; border: 1px solid #94a3b8; font-weight: 700; text-align: center; line-height: 14px; font-size: 9px; }
</style></head>
<body>
<h1>${title}</h1>
<table>
<thead><tr>${letterRow}</tr><tr>${numRow}</tr></thead>
<tbody>${bodyRows}</tbody>
</table>
<div class="legend">
  <span><span class="sw c-m">M</span>Mattina ${formatTurnoRange(turnoOrari.mattina)}</span>
  <span><span class="sw c-p">P</span>Pomeriggio ${formatTurnoRange(turnoOrari.pomeriggio)}</span>
  <span><span class="sw c-n">N</span>Notte ${formatTurnoRange(turnoOrari.notte)}</span>
  <span><span class="sw c-mp">M-P</span>Lunga ${formatTurnoRange(turnoOrari.lunga)}</span>
  <span><span class="sw c-ao">AO</span>Attività occupazionale</span>
  ${pomeriggioCortoConfig.enabled ? `<span><span class="sw c-pc">P*</span>Pomeriggio corto ${pomeriggioCortoConfig.ora_inizio?.replace(":00", "")}-${pomeriggioCortoConfig.ora_fine?.replace(":00", "")}</span>` : ""}
  <span><span class="sw c-sn">S</span>Smontante</span>
  <span><span class="sw c-sn">SN</span>Smonto notte</span>
  <span><span class="sw c-r">R</span>Riposo dopo notti</span>
  <span><span class="sw c-f">F</span>Ferie</span>
</div>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Consenti i popup per stampare il calendario");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
      w.onafterprint = () => w.close();
    }, 300);
  };

  const renderAltFigureCard = ({ figura, description }) => {
    const theme = getStaffFiguraTheme(figura);
    const FiguraIcon = theme.Icon;
    const list = listByFigura[figura] || [];
    const title = theme.label;
    const showSpec = isFiguraAttivita(figura);
    return (
      <Card className={`card-base mb-6 border-2 ${theme.cardBorder} bg-gradient-to-br ${theme.cardGradient}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 ${theme.titleColor}`}>
              <FiguraIcon className="w-5 h-5" />
              {title}
            </CardTitle>
            {hasPermission("users:update") && (
              <Button onClick={() => openOssModalCreate(figura)} className={theme.btnClass} size="sm">
                <FiguraIcon className="w-4 h-4 mr-1" />
                Aggiungi {title.toLowerCase()}
              </Button>
            )}
          </div>
          <p className={`text-sm ${theme.descColor}`}>{description}</p>
        </CardHeader>
        <CardContent>
          {loadingOss ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className={`w-6 h-6 animate-spin ${theme.spinnerColor}`} />
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-6">
              <FiguraIcon className={`w-10 h-10 mx-auto mb-2 opacity-30 ${theme.iconColor}`} />
              <p className="text-slate-500">Nessun operatore inserito</p>
              <p className="text-sm text-slate-400">Aggiungi il personale per il calendario turni</p>
            </div>
          ) : (
            <div className="space-y-2">
              {list.map((person) => (
                <div
                  key={person.id}
                  className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 bg-white rounded-lg border border-slate-100 border-l-2 ${theme.borderClass}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${theme.iconBg}`}>
                      <FiguraIcon className={`w-5 h-5 ${theme.iconColor}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {person.nome} {person.cognome}
                      </p>
                      {showSpec && person.specializzazione_label && (
                        <p className="text-xs font-medium text-slate-600 mt-0.5">
                          {person.specializzazione_label}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1 min-w-0">
                          <Mail className="w-3 h-3 shrink-0" />
                          {person.email?.trim() ? (
                            <span className="truncate">{person.email}</span>
                          ) : (
                            <span className="text-slate-400">Nessuna email</span>
                          )}
                        </span>
                        {person.telefono && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {person.telefono}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {showSpec
                          ? "Calendario attività/turni e invio via email"
                          : person.email?.trim()
                            ? "Riceve i turni via email"
                            : "Calendario turni (senza email)"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1 justify-end sm:shrink-0">
                    {hasPermission("users:update") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-600 hover:bg-slate-100"
                        onClick={() => openOssModalEdit(person)}
                        title="Modifica dati"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                    )}
                    {hasPermission("users:delete") && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteOss(person.id)}
                        title="Rimuovi"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 w-[90%] max-w-full mx-auto">
      {/* Page Header - responsive */}
      <PageHeader
        title="Personale"
        subtitle="Gestione del personale / candidati nella tua zona"
        icon={UserCog}
        iconContainerClassName="bg-gradient-to-br from-violet-500 to-violet-600"
        iconClassName="text-white"
      />

      {/* Plan Badge */}
      <div className="flex justify-end mb-4">
        {userPlan && (
          <Badge className={userPlan === "pro" ? "bg-amber-500 text-white" : "bg-blue-500 text-white"}>
            {userPlan === "pro" ? <Crown className="w-3 h-3 mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
            Piano {userPlan.toUpperCase()} attivo
          </Badge>
        )}
      </div>

      <Tabs defaultValue="oss" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md mb-4">
          <TabsTrigger value="oss" className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Gestione del personale
          </TabsTrigger>
          <TabsTrigger value="candidati" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Gestione candidati
            {candidati?.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">
                {candidati.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="oss" className="mt-0 w-full">
      {/* Calendario turni mensile – vista matrice (una riga per OSS) */}
      {calendarioTurniSections.length > 0 && (() => {
        const first = new Date(monthStart + "T12:00:00");
        const year = first.getFullYear();
        const month = first.getMonth();
        const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
        const daysInMonth = [];
        for (let i = 1; i <= lastDayOfMonth; i++) {
          const d = new Date(year, month, i);
          daysInMonth.push({
            dateStr: formatDateLocal(d),
            dayNum: i,
            dayLetter: getDayLetterIt(d),
            isSunday: d.getDay() === 0,
            isSaturday: d.getDay() === 6
          });
        }
        const colSpan = daysInMonth.length + 1;
        const renderTurniStaffRow = (oss) => {
          const rowTheme = getStaffFiguraTheme(staffFigura(oss));
          return (
          <tr key={oss.id}>
            <td
              className={`sticky left-0 z-10 border border-slate-300 border-l-2 ${rowTheme.borderClass} pl-1.5 pr-2 py-1 font-semibold text-slate-800 bg-white hover:bg-red-50/70 cursor-pointer transition-colors`}
              style={{ width: 148, minWidth: 148, borderLeftColor: rowTheme.borderColor, borderLeftWidth: 2 }}
              role="button"
              tabIndex={clearingTurniBusy ? -1 : 0}
              onClick={() =>
                openBulkClearRowDialog(oss.id, `${oss.cognome} ${oss.nome}`.trim() || String(oss.id))
              }
              onKeyDown={(e) => {
                if (clearingTurniBusy) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openBulkClearRowDialog(oss.id, `${oss.cognome} ${oss.nome}`.trim() || String(oss.id));
                }
              }}
              title={`${rowTheme.label} – clicca per rimuovere tutti i turni nel mese (le ferie restano).`}
            >
              <div className="flex items-start gap-1.5 min-w-0">
                {clearingRowOssId === String(oss.id) ? (
                  <Loader2 className="w-3.5 h-3.5 shrink-0 mt-0.5 animate-spin text-red-600" aria-hidden />
                ) : (
                  <StaffFiguraIcon figura={staffFigura(oss)} className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1 truncate">
                  <div className="truncate uppercase text-[11px] leading-tight">{oss.cognome}</div>
                  <div className="truncate text-[10px] font-normal text-slate-500 leading-tight">{oss.nome}</div>
                  {oss.specializzazione_label ? (
                    <div className="truncate text-[9px] font-normal text-slate-400 leading-tight" title={oss.specializzazione_label}>
                      {oss.specializzazione_label}
                    </div>
                  ) : null}
                </div>
              </div>
            </td>
            {daysInMonth.map((d) => {
              const info = getShiftInfoForOss(oss.id, d.dateStr);
              const baseBg = d.isSunday ? "bg-rose-50" : d.isSaturday ? "bg-slate-50" : "bg-white";
              const cellBg = info ? info.bg : baseBg;
              const cellText = info ? info.text : "text-slate-400";
              return (
                <td
                  key={`${oss.id}-${d.dateStr}`}
                  className={`border border-slate-300 text-center cursor-pointer hover:ring-2 hover:ring-sky-400 hover:ring-inset ${cellBg} ${cellText} transition-colors`}
                  style={{ width: 30, minWidth: 30, height: 30 }}
                  onClick={() => handleCellTurnoClick(oss.id, d.dateStr)}
                  title={info ? `${oss.nome} ${oss.cognome} – ${info.label}` : `Clicca per assegnare un turno a ${oss.nome} ${oss.cognome}`}
                >
                  {info ? (
                    <span className={`font-bold ${info.code.length > 1 ? "text-[9px]" : "text-[11px]"}`}>
                      {info.code}
                    </span>
                  ) : null}
                </td>
              );
            })}
          </tr>
          );
        };
        return (
          <Card className="card-base mb-6 border-2 border-sky-200 bg-white">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2 text-sky-700">
                  <Calendar className="w-5 h-5" />
                  Calendario turni
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const d = new Date(monthStart + "T12:00:00");
                      d.setMonth(d.getMonth() - 1);
                      setMonthStart(d.toISOString().slice(0, 7) + "-01");
                    }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium text-slate-700 min-w-[160px] text-center uppercase">
                    {first.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const d = new Date(monthStart + "T12:00:00");
                      d.setMonth(d.getMonth() + 1);
                      setMonthStart(d.toISOString().slice(0, 7) + "-01");
                    }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-1 border-amber-200 text-amber-800 hover:bg-amber-50"
                    onClick={() => openFerieModal()}
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Inserisci ferie
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-sky-600 hover:bg-sky-700 ml-1"
                    onClick={() => openTurniAutoDialog("genera")}
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Genera turni del mese
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    onClick={() => openTurniAutoDialog("controlla")}
                    disabled={loadingControlla || loadingTurni}
                    title="Verifica coerenza e distribuzione dei turni del mese"
                  >
                    {loadingControlla ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin shrink-0" />
                    ) : (
                      <ClipboardCheck className="w-4 h-4 mr-1 shrink-0" />
                    )}
                    Controlla turni
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-1 border-red-300 text-red-700 hover:bg-red-50"
                    disabled={clearingTurniBusy || loadingTurni}
                    onClick={openBulkClearMonthDialog}
                    title="Elimina tutte le assegnazioni turni del mese (tutti gli OSS). Chiede conferma."
                  >
                    {clearingMonthTurni ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin shrink-0" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-1 shrink-0" />
                    )}
                    Svuota mese
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-1"
                    onClick={handlePrintCalendarioTurni}
                    title="Stampa il calendario turni del mese selezionato"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Stampa
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    className="ml-1 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                    onClick={handleEmailTurniMese}
                    disabled={sendingTurniMeseEmail}
                    title="Invia a ogni operatore con email (OSS, pulizie, cuoco, educatore, terapista) l'elenco turni del mese"
                  >
                    {sendingTurniMeseEmail ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-1" />
                    )}
                    Invia mail turni
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap mt-2 pt-2 border-t border-slate-100">
                <Label htmlFor="schema-orario-turni" className="text-slate-700 text-xs font-medium shrink-0">
                  Orari turni struttura
                </Label>
                <Select
                  value={schemaOrarioAttivo}
                  onValueChange={handleSchemaOrarioChange}
                  disabled={savingTurniConfig || loadingTurni}
                >
                  <SelectTrigger id="schema-orario-turni" className="w-full max-w-md h-8 text-xs">
                    <SelectValue placeholder="Seleziona orari turni" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHEMA_ORARIO_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label} — {opt.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {savingTurniConfig && <Loader2 className="w-4 h-4 animate-spin text-sky-600" aria-hidden />}
                <span className="text-xs text-slate-500">
                  Default: turni da 7 ore. Usato per legenda, assegnazioni manuali e area OSS.
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600 mt-1">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-sm bg-sky-200 border border-sky-300 text-center leading-4 font-bold text-sky-900">M</span>
                  Mattina {formatTurnoRange(turnoOrari.mattina)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-sm bg-rose-200 border border-rose-300 text-center leading-4 font-bold text-rose-900">P</span>
                  Pomeriggio {formatTurnoRange(turnoOrari.pomeriggio)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-sm bg-slate-300 border border-slate-400 text-center leading-4 font-bold text-slate-800">N</span>
                  Notte {formatTurnoRange(turnoOrari.notte)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-auto px-1 h-4 rounded-sm bg-emerald-300 border border-emerald-400 text-center leading-4 font-bold text-emerald-900 text-[10px]">M-P</span>
                  Lunga {formatTurnoRange(turnoOrari.lunga)}
                </span>
                {pomeriggioCortoConfig.enabled && (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-auto px-1 h-4 rounded-sm bg-fuchsia-200 border border-fuchsia-300 text-center leading-4 font-bold text-fuchsia-900 text-[10px]">P*</span>
                    Pomeriggio corto {pomeriggioCortoConfig.ora_inizio?.replace(":00", "")}-{pomeriggioCortoConfig.ora_fine?.replace(":00", "")}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-sm bg-indigo-200 border border-indigo-300 text-center leading-4 font-bold text-indigo-900">S</span>
                  Smontante
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-auto px-1 h-4 rounded-sm bg-indigo-200 border border-indigo-300 text-center leading-4 font-bold text-indigo-900 text-[10px]">SN</span>
                  Smonto notte
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-sm bg-violet-200 border border-violet-300 text-center leading-4 font-bold text-violet-900">R</span>
                  Riposo dopo notti
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-sm bg-amber-200 border border-amber-300 text-center leading-4 font-bold text-amber-900">F</span>
                  Ferie
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-auto px-1 h-4 rounded-sm bg-lime-200 border border-lime-300 text-center leading-4 font-bold text-lime-900 text-[10px]">AO</span>
                  Attività occupazionale (educatore/terapista)
                </span>
                <span className="text-slate-400">
                  · {turniConfig.schema_orario === "sei_ore"
                    ? "Schema 6 ore: S/R salvati dal ciclo NNSRLP."
                    : `Smonto/riposo calcolati dopo ${regolaMaxNottiCalendario} notti per ${regolaRiposoNottiCalendario} giorn${regolaRiposoNottiCalendario === 1 ? "o" : "i"}.`}
                </span>
                <span className="text-slate-400">Clicca su una cella giorno per assegnare o modificare il turno.</span>
                <span className="text-slate-400">Nome (prima colonna): clicca per svuotare tutti i turni della riga nel mese.</span>
                <span className="w-full h-px bg-slate-200 my-0.5" aria-hidden />
                {[
                  STAFF_FIGURA_OSS,
                  STAFF_FIGURA_PULIZIE,
                  STAFF_FIGURA_CUOCO,
                  STAFF_FIGURA_EDUCATORE,
                  STAFF_FIGURA_TERAPISTA,
                ].map((f) => {
                  const t = getStaffFiguraTheme(f);
                  const FiguraIcon = t.Icon;
                  return (
                    <span key={f} className="inline-flex items-center gap-1.5">
                      <span
                        className="inline-block w-0.5 h-3.5 rounded-full shrink-0"
                        style={{ width: 2, backgroundColor: t.borderColor }}
                        aria-hidden
                      />
                      <FiguraIcon className={`w-3.5 h-3.5 ${t.iconColor}`} aria-hidden />
                      {t.label}
                    </span>
                  );
                })}
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4">
              {Object.keys(pendingTurnoChanges).length > 0 && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-medium text-amber-800 mb-2">Modifiche turni non ancora comunicate agli OSS:</p>
                  <ul className="text-sm text-amber-700 space-y-1 mb-3">
                    {Object.entries(pendingTurnoChanges).map(([ossId, arr]) => {
                      const oss = findStaffById(ossId);
                      const name = oss
                        ? `${oss.nome} ${oss.cognome} (${staffFiguraLabel(staffFigura(oss))})`
                        : `Operatore ${ossId}`;
                      const n = arr?.length || 0;
                      return (
                        <li key={ossId}>
                          <span className="font-medium">{name}</span>: {n} modific{n !== 1 ? "he" : "a"} (assegnati/rimossi)
                        </li>
                      );
                    })}
                  </ul>
                  <Button
                    size="sm"
                    onClick={handleSendTurnoSummary}
                    disabled={sendingTurnoSummary}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {sendingTurnoSummary ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                    Invia email riepilogo agli OSS
                  </Button>
                </div>
              )}
              {loadingTurni ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
                </div>
              ) : (
                <>
                <div className={`overflow-x-auto -mx-1 transition-opacity ${clearingTurniBusy ? "pointer-events-none opacity-70" : ""}`}>
                  <table className="border-collapse text-xs" style={{ tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th
                          rowSpan={2}
                          className="sticky left-0 z-20 bg-slate-100 border border-slate-300 px-2 py-1 text-left font-semibold text-slate-700 align-middle"
                          style={{ width: 148, minWidth: 148 }}
                        >
                          Personale
                        </th>
                        {daysInMonth.map((d) => (
                          <th
                            key={`dl-${d.dateStr}`}
                            className={`border border-slate-300 px-0 py-1 text-center font-semibold ${d.isSunday ? "bg-rose-100 text-rose-700" : d.isSaturday ? "bg-slate-50 text-slate-700" : "bg-slate-100 text-slate-700"}`}
                            style={{ width: 30, minWidth: 30 }}
                          >
                            {d.dayLetter}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        {daysInMonth.map((d) => (
                          <th
                            key={`dn-${d.dateStr}`}
                            className={`border border-slate-300 px-0 py-1 text-center font-semibold ${d.isSunday ? "bg-rose-100 text-rose-700" : d.isSaturday ? "bg-slate-50 text-slate-700" : "bg-slate-100 text-slate-700"}`}
                            style={{ width: 30, minWidth: 30 }}
                          >
                            {d.dayNum}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {calendarioTurniSections.map((section, sectionIdx) => (
                        <Fragment key={section.key}>
                          {sectionIdx > 0 ? (
                            <tr className="bg-slate-50" aria-hidden>
                              <td colSpan={colSpan} className="p-0 border-x border-slate-300">
                                <div className="h-2 border-t-2 border-dashed border-slate-300 bg-slate-100/90" />
                              </td>
                            </tr>
                          ) : null}
                          {section.rows.map((oss) => renderTurniStaffRow(oss))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {ferie.length > 0 && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-medium text-amber-900 mb-2">Ferie nel periodo visualizzato</p>
                    <div className="flex flex-wrap gap-2">
                      {ferie.map((f) => {
                        const oss = findStaffById(f.oss_id);
                        const name = oss ? `${oss.nome} ${oss.cognome}` : "OSS";
                        return (
                          <span
                            key={f.id}
                            className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1 text-xs text-amber-900"
                          >
                            <span className="font-medium">{name}</span>
                            <span>{f.from_date === f.to_date ? f.from_date : `${f.from_date} - ${f.to_date}`}</span>
                            <button
                              type="button"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteFerie(f.id)}
                              title="Rimuovi ferie"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                </>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* SEZIONE: ELENCO OSS (per calendario turni) */}
      <Card className="card-base mb-6 border-2 border-sky-200 bg-gradient-to-br from-sky-50 to-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sky-700">
              <HeartHandshake className="w-5 h-5" />
              Elenco OSS
            </CardTitle>
            {hasPermission("users:update") && (
            <Button
              onClick={() => openOssModalCreate(STAFF_FIGURA_OSS)}
              className="bg-sky-600 hover:bg-sky-700"
              size="sm"
            >
              <HeartHandshake className="w-4 h-4 mr-1" />
              Aggiungi OSS
            </Button>
            )}
          </div>
          <p className="text-sm text-sky-600">
            Operatori Socio-Sanitari della struttura.
          </p>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-sky-200 bg-sky-50/90 px-4 py-3 mb-4 text-sm text-slate-700 space-y-2">
            <p className="font-medium text-sky-900">Come accedono gli OSS all&apos;area riservata</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              <li>
                Pagina dedicata (diversa dal login struttura):{" "}
                <span className="font-medium text-slate-800">email + password</span> oppure{" "}
                <span className="font-medium text-slate-800">codice univoco + password</span>.
              </li>
              <li>
                In <strong>Aggiungi OSS</strong> scegli la modalità di accesso. Con il codice, Affidya lo genera
                automaticamente: annotalo e comunicalo all&apos;operatore (non è modificabile a mano).
              </li>
              <li>
                Con email puoi spuntare l&apos;invio automatico di link e password. Il pulsante &quot;Invia accesso via
                email&quot; sull&apos;elenco invia solo un promemoria senza password.
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
              <code className="text-xs bg-white/80 border border-sky-100 rounded px-2 py-1.5 break-all flex-1">
                {ossLoginPageUrl}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 border-sky-200 text-sky-800 hover:bg-sky-100"
                onClick={() => {
                  navigator.clipboard.writeText(ossLoginPageUrl).then(
                    () => toast.success("Link copiato negli appunti"),
                    () => toast.error("Impossibile copiare il link")
                  );
                }}
              >
                <Copy className="w-4 h-4 mr-1" />
                Copia link
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              L&apos;invio dall&apos;elenco non modifica la password. Per reinviarla in chiaro usa <strong>Modifica</strong>{" "}
              (con nuova password + invio email) oppure crea l&apos;OSS con l&apos;opzione email attiva.
            </p>
          </div>
          {loadingOss ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
            </div>
          ) : ossList.length === 0 ? (
            <div className="text-center py-6">
              <HeartHandshake className="w-10 h-10 text-sky-300 mx-auto mb-2" />
              <p className="text-slate-500">Nessun OSS inserito</p>
              <p className="text-sm text-slate-400">Aggiungi gli operatori per gestire il calendario turni</p>
              <Button
                variant="outline"
                className="mt-3 border-sky-300 text-sky-700 hover:bg-sky-50"
                onClick={() => openOssModalCreate(STAFF_FIGURA_OSS)}
              >
                <HeartHandshake className="w-4 h-4 mr-2" />
                Aggiungi il primo OSS
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {ossOnlyList.map((oss) => (
                <div
                  key={oss.id}
                  className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 bg-white rounded-lg border border-slate-100 border-l-2 border-l-sky-500 hover:border-sky-100"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                      <HeartHandshake className="w-5 h-5 text-sky-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 flex flex-wrap items-center gap-2">
                        <span>{oss.nome} {oss.cognome}</span>
                        {oss.responsabile ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-medium">
                            Responsabile
                          </Badge>
                        ) : null}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                        {oss.login_code ? (
                          <span className="flex items-center gap-1 min-w-0 font-mono text-sky-800">
                            <KeyRound className="w-3 h-3 shrink-0" />
                            <span className="truncate">{oss.login_code}</span>
                          </span>
                        ) : null}
                        {oss.email?.trim() ? (
                          <span className="flex items-center gap-1 min-w-0">
                            <Mail className="w-3 h-3 shrink-0" />
                            <span className="truncate">{oss.email}</span>
                          </span>
                        ) : !oss.login_code ? (
                          <span className="text-slate-400">Nessuna email</span>
                        ) : null}
                        {oss.telefono && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {oss.telefono}
                          </span>
                        )}
                        {((oss.preferenze_turni && (oss.preferenze_turni.mattina || oss.preferenze_turni.pomeriggio || oss.preferenze_turni.notte))) && (
                          <span className="text-xs text-sky-600">Preferenze turni impostate</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1 justify-end sm:justify-end sm:shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-sky-200 text-sky-800 hover:bg-sky-50"
                      disabled={!oss.email?.trim() || sendingOssAccessId === oss.id}
                      onClick={() => handleInviaEmailAccessoOss(oss)}
                      title={!oss.email?.trim() ? "Aggiungi un'email all'OSS" : "Invia link e istruzioni di accesso (senza modificare la password)"}
                    >
                      {sendingOssAccessId === oss.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-1" />
                      )}
                      Invia accesso via email
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-amber-700 hover:bg-amber-50"
                      onClick={() => setDocumentiStaff(oss)}
                      title="Documenti e attestati"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Documenti
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-sky-600 hover:bg-sky-50"
                      onClick={() => {
                        const p = oss.preferenze_turni || {};
                        setPreferenzeForm({
                          mattina: p.mattina || "",
                          pomeriggio: p.pomeriggio || "",
                          notte: p.notte || ""
                        });
                        setOssPreferenzeModal(oss);
                      }}
                      title="Preferenze turni"
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Preferenze
                    </Button>
                    {hasPermission("users:update") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-600 hover:bg-slate-100"
                      onClick={() => openOssModalEdit(oss)}
                      title="Modifica dati"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Modifica
                    </Button>
                    )}
                    {hasPermission("users:delete") && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteOss(oss.id)}
                      title="Rimuovi OSS"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {renderAltFigureCard({
        figura: STAFF_FIGURA_PULIZIE,
        description:
          "Calendario turni e ferie. Email opzionale per l'invio dei turni. Nessun accesso alla piattaforma."
      })}

      {renderAltFigureCard({
        figura: STAFF_FIGURA_CUOCO,
        description:
          "Calendario turni e ferie. Email opzionale per l'invio dei turni. Nessun accesso alla piattaforma."
      })}

      {renderAltFigureCard({
        figura: STAFF_FIGURA_EDUCATORE,
        description:
          "Calendario con attività occupazionali (AO) e presenza M/P. Indica la specializzazione. Nessun accesso alla piattaforma."
      })}

      {renderAltFigureCard({
        figura: STAFF_FIGURA_TERAPISTA,
        description:
          "Calendario con attività occupazionali (AO) e presenza M/P. Indica la specializzazione. Nessun accesso alla piattaforma."
      })}

      {/* Conferma svuotamento calendario turni (mese o riga) */}
      <Dialog
        open={!!bulkClearDialog}
        onOpenChange={(open) => {
          if (!open) setBulkClearDialog(null);
        }}
      >
        <DialogContent className="max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {bulkClearDialog?.type === "month"
                ? "Svuotare tutto il mese?"
                : bulkClearDialog?.type === "row"
                  ? "Svuotare la riga?"
                  : "Conferma"}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-1 text-left text-muted-foreground">
              {bulkClearDialog?.type === "month" && (
                <>
                  <p>
                    Stai per eliminare tutti i{" "}
                    <span className="font-semibold text-slate-800">{bulkClearDialog.count}</span> turni del mese{" "}
                    <span className="font-semibold text-slate-800">{bulkClearDialog.monthLabelPretty}</span>, per tutto il
                    personale in calendario.
                  </p>
                  <p>Le ferie registrate non verranno modificate.</p>
                </>
              )}
              {bulkClearDialog?.type === "row" && (
                <>
                  <p>
                    Rimuovere tutti i <span className="font-semibold text-slate-800">{bulkClearDialog.count}</span>{" "}
                    turni di <span className="font-semibold text-slate-800">{bulkClearDialog.nominativo}</span> per{" "}
                    <span className="font-semibold text-slate-800">{bulkClearDialog.monthLabelPretty}</span>?
                  </p>
                  <p>Le ferie di questo operatore non verranno modificate.</p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setBulkClearDialog(null)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={confirmBulkClearDialog}>
              Elimina turni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Preferenze turni OSS */}
      <Dialog open={!!ossPreferenzeModal} onOpenChange={(open) => !open && setOssPreferenzeModal(null)}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Preferenze turni</DialogTitle>
            <DialogDescription>
              {ossPreferenzeModal && (
                <>
                  <span className="font-medium text-slate-700">{ossPreferenzeModal.nome} {ossPreferenzeModal.cognome}</span>
                  <br />
                  Le preferenze sono considerate nella generazione automatica ma non vincolanti: se possibile si assegna in base a esse, altrimenti si procede comunque.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {[
              { key: "mattina", label: `Turno mattina (${formatTurnoRange(turnoOrari.mattina)})`, desc: "Preferenza per essere assegnato o non assegnato al turno mattina." },
              { key: "pomeriggio", label: `Turno pomeriggio (${formatTurnoRange(turnoOrari.pomeriggio)})`, desc: "Preferenza per il turno pomeriggio." },
              { key: "notte", label: `Turno sera (${formatTurnoRange(turnoOrari.notte)})`, desc: "Preferenza per il turno sera/notte." }
            ].map(({ key, label, desc }) => (
              <div key={key}>
                <Label className="text-slate-700">{label}</Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-1">{desc}</p>
                <Select
                  value={preferenzeForm[key] || "nessuna"}
                  onValueChange={(v) => setPreferenzeForm((prev) => ({ ...prev, [key]: v === "nessuna" ? "" : v }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Nessuna preferenza" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nessuna">Nessuna preferenza</SelectItem>
                    <SelectItem value="si">Sì — preferisco essere assegnato</SelectItem>
                    <SelectItem value="no">No — preferisco non essere assegnato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOssPreferenzeModal(null)}>Annulla</Button>
            <Button onClick={handleSavePreferenze} disabled={savingPreferenze} className="bg-sky-600 hover:bg-sky-700">
              {savingPreferenze ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Salva preferenze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modifica turno (vista matrice OSS × giorni) */}
      <Dialog open={showCellTurnoModal} onOpenChange={(open) => {
        setShowCellTurnoModal(open);
        if (!open) setCellTurnoData({ ossId: null, data: null });
      }}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              {(() => {
                const oss = findStaffById(cellTurnoData.ossId);
                return isFiguraAttivita(oss) ? "Turno / attività" : "Turno";
              })()}
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const oss = ossList.find((o) => String(o.id) === String(cellTurnoData.ossId));
                const dateStr = cellTurnoData.data;
                if (!oss || !dateStr) return null;
                const dateLabel = new Date(dateStr + "T12:00:00").toLocaleDateString("it-IT", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                });
                return (
                  <span>
                    <span className="font-semibold">{oss.nome} {oss.cognome}</span>
                    {oss.specializzazione_label ? (
                      <span className="text-slate-500"> · {oss.specializzazione_label}</span>
                    ) : null}
                    {" · "}{dateLabel}
                  </span>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const cellStaff = findStaffById(cellTurnoData.ossId);
            const cellIsAttivita = isFiguraAttivita(cellStaff);
            const currentFerie = cellTurnoData.ossId && cellTurnoData.data
              ? getFerieForOssDate(cellTurnoData.ossId, cellTurnoData.data)
              : null;
            const currentInfo = cellTurnoData.ossId && cellTurnoData.data
              ? getShiftInfoForOss(cellTurnoData.ossId, cellTurnoData.data)
              : null;
            const currentType = currentInfo?.type || null;
            const options = cellIsAttivita
              ? [
                  {
                    type: TURNO_ATTIVITA_OCCUPAZIONALE,
                    code: "AO",
                    label: "Attività occupazionale",
                    bg: "bg-lime-200 hover:bg-lime-300",
                    text: "text-lime-900",
                    ring: "ring-lime-600",
                  },
                  { type: "mattina", code: "M", label: `Mattina ${formatTurnoRange(turnoOrari.mattina)}`, bg: "bg-sky-200 hover:bg-sky-300", text: "text-sky-900", ring: "ring-sky-500" },
                  { type: "pomeriggio", code: "P", label: `Pomeriggio ${formatTurnoRange(turnoOrari.pomeriggio)}`, bg: "bg-rose-200 hover:bg-rose-300", text: "text-rose-900", ring: "ring-rose-500" },
                  { type: "lunga", code: "M-P", label: `Lunga ${formatTurnoRange(turnoOrari.lunga)}`, bg: "bg-emerald-300 hover:bg-emerald-400", text: "text-emerald-900", ring: "ring-emerald-500" },
                ]
              : [
                  { type: "mattina", code: "M", label: `Mattina ${formatTurnoRange(turnoOrari.mattina)}`, bg: "bg-sky-200 hover:bg-sky-300", text: "text-sky-900", ring: "ring-sky-500" },
                  { type: "pomeriggio", code: "P", label: `Pomeriggio ${formatTurnoRange(turnoOrari.pomeriggio)}`, bg: "bg-rose-200 hover:bg-rose-300", text: "text-rose-900", ring: "ring-rose-500" },
                  { type: "notte", code: "N", label: `Notte ${formatTurnoRange(turnoOrari.notte)}`, bg: "bg-slate-300 hover:bg-slate-400", text: "text-slate-900", ring: "ring-slate-500" },
                  { type: "lunga", code: "M-P", label: `Lunga ${formatTurnoRange(turnoOrari.lunga)}`, bg: "bg-emerald-300 hover:bg-emerald-400", text: "text-emerald-900", ring: "ring-emerald-500" },
                ];
            return (
              <div className="py-2 space-y-3">
                {currentFerie && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="font-medium">Ferie impostate per questo giorno.</p>
                    <p className="text-xs mt-1">
                      Periodo: {currentFerie.from_date} - {currentFerie.to_date}
                      {currentFerie.note ? ` · ${currentFerie.note}` : ""}
                    </p>
                  </div>
                )}
                {!currentFerie && (
                  <div className="grid grid-cols-2 gap-2">
                    {options.map((opt) => {
                      const isActive = currentType === opt.type;
                      return (
                        <button
                          key={opt.type}
                          type="button"
                          disabled={savingCellTurno}
                          onClick={() => handleSetCellTurno(opt.type)}
                          className={`rounded-md border border-slate-300 px-3 py-3 text-sm font-medium transition-all ${opt.bg} ${opt.text} ${isActive ? `ring-2 ${opt.ring}` : ""} disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          <div className="text-base font-bold">{opt.code}</div>
                          <div className="text-xs font-normal">{opt.label}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {!currentFerie && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-amber-200 text-amber-800 hover:bg-amber-50"
                    disabled={savingCellTurno}
                    onClick={() => {
                      openFerieModal({ ossId: cellTurnoData.ossId, date: cellTurnoData.data });
                      setShowCellTurnoModal(false);
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Ferie in questo giorno
                  </Button>
                )}
                {currentFerie && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-red-200 text-red-700 hover:bg-red-50"
                    disabled={savingCellTurno}
                    onClick={() => {
                      setShowCellTurnoModal(false);
                      handleDeleteFerie(currentFerie.id);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Rimuovi ferie
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-red-200 text-red-700 hover:bg-red-50"
                  disabled={savingCellTurno || !currentInfo || !!currentFerie}
                  onClick={() => handleSetCellTurno(null)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {staffIdIsOss(cellTurnoData.ossId) ? "Nessun turno (riposo)" : "Nessun turno"}
                </Button>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCellTurnoModal(false); setCellTurnoData({ ossId: null, data: null }); }} disabled={savingCellTurno}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Assegna turno */}
      <Dialog open={showTurnoModal} onOpenChange={(open) => {
        setShowTurnoModal(open);
        if (!open) setSelectedOssIdsForTurno([]);
      }}>
        <DialogContent key={turnoModalData.data + turnoModalData.turno || "turno"} className="max-w-[340px]">
          <DialogHeader>
            <DialogTitle>Assegna turno</DialogTitle>
            <DialogDescription>
              {turnoModalData.data && (
                <span>
                  {new Date(turnoModalData.data + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}{" "}
                  – {turnoModalData.turno}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-slate-700">Operatori (seleziona uno o più)</Label>
            <div className="mt-2 max-h-[220px] overflow-y-auto rounded-md border border-slate-200 p-2 space-y-1.5">
              {ossList.map((o) => (
                <label
                  key={o.id}
                  className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedOssIdsForTurno.includes(o.id)}
                    onCheckedChange={() => toggleOssForTurno(o.id)}
                  />
                  <span className="text-sm inline-flex items-center gap-1.5">
                    <StaffFiguraIcon figura={staffFigura(o)} className="w-3.5 h-3.5" />
                    {o.nome} {o.cognome}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTurnoModal(false); setTurnoModalData({ data: null, turno: null }); setSelectedOssIdsForTurno([]); }}>
              Annulla
            </Button>
            <Button onClick={() => handleSaveTurno(selectedOssIdsForTurno)} disabled={selectedOssIdsForTurno.length === 0}>
              Assegna turno{selectedOssIdsForTurno.length > 1 ? ` (${selectedOssIdsForTurno.length})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Inserisci ferie */}
      <Dialog open={showFerieModal} onOpenChange={(open) => {
        setShowFerieModal(open);
        if (!open) setFerieForm({ oss_id: "", from_date: "", to_date: "", note: "" });
      }}>
        <DialogContent className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Inserisci ferie</DialogTitle>
            <DialogDescription>
              Seleziona un operatore (OSS, pulizie, cuoco, educatore o terapista) e un intervallo. Eventuali turni già assegnati in quei giorni vengono rimossi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-slate-700">Operatore</Label>
              <Select
                value={ferieForm.oss_id || ""}
                onValueChange={(value) => setFerieForm((prev) => ({ ...prev, oss_id: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona operatore" />
                </SelectTrigger>
                <SelectContent>
                  {calendarioTurniSections.map((section) => (
                    <SelectGroup key={section.key}>
                      <SelectLabel>{staffFiguraLabel(section.key)}</SelectLabel>
                      {section.rows.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.nome} {o.cognome}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-700">Dal</Label>
                <Input
                  type="date"
                  value={ferieForm.from_date}
                  onChange={(e) => setFerieForm((prev) => ({ ...prev, from_date: e.target.value, to_date: prev.to_date || e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-700">Al</Label>
                <Input
                  type="date"
                  value={ferieForm.to_date}
                  onChange={(e) => setFerieForm((prev) => ({ ...prev, to_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Note</Label>
              <Textarea
                value={ferieForm.note}
                onChange={(e) => setFerieForm((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="Opzionale"
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFerieModal(false)} disabled={savingFerie}>
              Annulla
            </Button>
            <Button onClick={handleSaveFerie} disabled={savingFerie} className="bg-amber-600 hover:bg-amber-700">
              {savingFerie ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Salva ferie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal generazione / controllo turni del mese */}
      <Dialog open={showGeneraTurniModal} onOpenChange={setShowGeneraTurniModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {turniAutoDialogMode === "controlla" ? "Controllo turni del mese" : "Genera turni del mese"}
            </DialogTitle>
            <DialogDescription>
              {turniAutoDialogMode === "controlla"
                ? "Confronta i turni già presenti nel calendario con le impostazioni sotto: copertura, vincoli, ferie e distribuzione tra gli OSS selezionati."
                : generaModalita === "schema_ppmmnnsr"
                  ? "Ogni OSS segue il ciclo fisso PPMMNNSR (2 pomeriggio, 2 mattina, 2 notte, smonto notte, riposo). Gli operatori sono sfalsati per distribuire la copertura. Rispetta ferie e assenze la domenica. Con almeno 4 OSS la copertura giornaliera è completa."
                  : generaModalita === "schema_nnsrlp" || generaModalita === "schema_nnlpsr"
                    ? "Ogni OSS segue il ciclo fisso N N S R L P: 2 notti, smontante, riposo, lunga e pomeriggio. Usa il profilo orari 6 ore e imposta automaticamente il pomeriggio corto 14-20 a rotazione giornaliera."
                    : "Genera automaticamente le assegnazioni rispettando ferie impostate, notti consecutive, smonto notte/riposo, turni lunghi, riposi e assenze la domenica. Puoi indicare OSS solo mattina, solo pomeriggio o solo notte (generazione automatica). Opzionalmente riduci le coperture in domenica e festività nazionali."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {turniAutoDialogMode === "genera" && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 space-y-2">
                <Label className="text-slate-700 font-medium">Template della struttura</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={selectedTurniTemplateId} onValueChange={applyTurniTemplate}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Scegli un template salvato" /></SelectTrigger>
                    <SelectContent>
                      {turniTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.nome}{template.predefinito ? " (predefinito)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={turniTemplateName}
                    onChange={(event) => setTurniTemplateName(event.target.value)}
                    placeholder="Nome nuovo template"
                    className="bg-white"
                  />
                  <Button type="button" variant="outline" onClick={saveTurniTemplate} disabled={savingTurniTemplate}>
                    Salva configurazione
                  </Button>
                  {selectedTurniTemplateId && (
                    <Button type="button" variant="ghost" onClick={setTurniTemplateDefault}>
                      Imposta predefinito
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Salva coperture, vincoli, operatori selezionati e istruzioni per riutilizzarli ogni mese.
                </p>
              </div>
            )}
            <div>
              <Label className="text-slate-700 font-medium">Modalità di generazione</Label>
              <RadioGroup
                value={generaModalita}
                onValueChange={setGeneraModalita}
                className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-3"
              >
                <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50 has-[:checked]:border-sky-400 has-[:checked]:bg-sky-50/50">
                  <RadioGroupItem value="copertura" id="genera-copertura" className="mt-0.5" />
                  <span>
                    <span className="text-sm font-medium text-slate-800">Copertura giornaliera</span>
                    <p className="text-xs text-slate-500 font-normal mt-0.5">
                      Riempie gli slot mattina, pomeriggio e notte per ogni giorno con le regole avanzate.
                    </p>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50 has-[:checked]:border-sky-400 has-[:checked]:bg-sky-50/50">
                  <RadioGroupItem value="schema_ppmmnnsr" id="genera-schema-ppmmnnsr" className="mt-0.5" />
                  <span>
                    <span className="text-sm font-medium text-slate-800">Schema PPMMNNSR</span>
                    <p className="text-xs text-slate-500 font-normal mt-0.5">
                      Ciclo fisso per operatore: P P M M N N S R (pomeriggio, mattina, notte, smonto, riposo).
                    </p>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50 has-[:checked]:border-sky-400 has-[:checked]:bg-sky-50/50">
                  <RadioGroupItem value="schema_nnsrlp" id="genera-schema-nnsrlp" className="mt-0.5" />
                  <span>
                    <span className="text-sm font-medium text-slate-800">Schema 6 ore NNSRLP</span>
                    <p className="text-xs text-slate-500 font-normal mt-0.5">
                      Ciclo N N S R L P con orari 7-13, 13-19, 19-7, lunga 7-19 e pomeriggio corto 14-20 a rotazione.
                    </p>
                  </span>
                </label>
              </RadioGroup>
            </div>

            {/* Prima riga: Mese + Sostituisci */}
            <div className={`grid grid-cols-1 gap-4 items-end ${turniAutoDialogMode === "controlla" ? "" : "sm:grid-cols-[1fr_auto]"}`}>
              <div>
                <Label className="text-slate-700 font-medium">
                  {turniAutoDialogMode === "controlla" ? "Mese da controllare" : "Mese da generare"}
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {turniAutoDialogMode === "controlla"
                    ? "Scegli il mese di cui verificare i turni già presenti."
                    : "Scegli il mese per cui creare le assegnazioni turni."}
                </p>
                <Input
                  type="month"
                  value={generaMonth ? generaMonth.slice(0, 7) : ""}
                  onChange={(e) => setGeneraMonth(e.target.value ? e.target.value + "-01" : monthStart)}
                  className="mt-1.5 max-w-[180px]"
                />
              </div>
              {turniAutoDialogMode === "genera" && (
                <label className="flex items-center gap-2 cursor-pointer pb-1 sm:pb-0">
                  <Checkbox
                    checked={generaSostituisci}
                    onCheckedChange={setGeneraSostituisci}
                  />
                  <span className="text-sm text-slate-700">Sostituisci turni esistenti nel mese</span>
                </label>
              )}
            </div>
            {turniAutoDialogMode === "genera" && (
            <p className="text-xs text-slate-500 -mt-2">Se attivo, i turni già presenti nel mese vengono eliminati e sostituiti con i nuovi. Se disattivo, le nuove assegnazioni si aggiungono solo dove non c&apos;è già copertura.</p>
            )}
            {turniAutoDialogMode === "genera" && !generaSostituisci && (
              <div className="-mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={generaUsaEsistentiBase}
                    onCheckedChange={setGeneraUsaEsistentiBase}
                  />
                  <span className="text-sm text-slate-700">Usa i turni già inseriti come base</span>
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  Se attivo, i turni già inseriti nel mese (manuali o precedenti) vengono considerati come punto di
                  partenza per notti consecutive, riposi, schemi fissi e istruzioni aggiuntive: la generazione
                  completa il resto del mese senza mai modificarli. Se disattivo, contano solo come slot già
                  occupati.
                </p>
              </div>
            )}

            {/* OSS da includere | Chi può fare la lunga | Non presenti di domenica */}
            <div className={`grid grid-cols-1 gap-5 ${generaModalita === "schema_ppmmnnsr" ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
              <div>
                <Label className="text-slate-700 font-medium">OSS da includere</Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-2">Seleziona gli operatori che riceveranno turni nel mese. È obbligatorio sceglierne almeno uno.</p>
                <div className="max-h-[200px] overflow-y-auto rounded-md border border-slate-200 p-2.5 space-y-1.5 bg-white">
                  {ossOnlyList.map((o) => (
                    <label
                      key={o.id}
                      className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={generaOssIds.includes(o.id)}
                        onCheckedChange={(checked) => {
                          setGeneraOssIds((prev) =>
                            checked ? [...prev, o.id] : prev.filter((id) => id !== o.id)
                          );
                          if (checked) {
                            setGeneraOssIdsLunghe((prev) => (prev.includes(o.id) ? prev : [...prev, o.id]));
                          } else {
                            setGeneraOssIdsLunghe((prev) => prev.filter((id) => id !== o.id));
                            setGeneraOssIdsAssentiDomenica((prev) => prev.filter((id) => id !== o.id));
                            setGeneraOssIdsSoloMattina((prev) => prev.filter((id) => id !== o.id));
                            setGeneraOssIdsSoloPomeriggio((prev) => prev.filter((id) => id !== o.id));
                            setGeneraOssIdsSoloNotte((prev) => prev.filter((id) => id !== o.id));
                          }
                        }}
                      />
                      <span className="text-sm">{o.nome} {o.cognome}</span>
                    </label>
                  ))}
                </div>
              </div>
              {generaOssIds.length > 0 && generaModalita === "copertura" && (
                <div>
                  <Label className="text-slate-700 font-medium">Chi può fare la lunga ({formatTurnoRange(turnoOrari.lunga)})</Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">Il turno lungo unisce mattina e pomeriggio nello stesso giorno; dopo c&apos;è riposo. Indica quali OSS possono essere assegnati a questo tipo di turno. Non compatibile con &quot;solo mattina&quot;, &quot;solo pomeriggio&quot; o &quot;solo notte&quot;.</p>
                  <div className="max-h-[200px] overflow-y-auto rounded-md border border-slate-200 p-2.5 space-y-1.5 bg-slate-50/50">
                    {ossOnlyList.filter((o) => generaOssIds.includes(o.id)).map((o) => (
                      <label
                        key={o.id}
                        className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white cursor-pointer"
                      >
                        <Checkbox
                          checked={generaOssIdsLunghe.includes(o.id)}
                          disabled={
                            generaOssIdsSoloMattina.includes(o.id) ||
                            generaOssIdsSoloPomeriggio.includes(o.id) ||
                            generaOssIdsSoloNotte.includes(o.id)
                          }
                          onCheckedChange={(checked) => {
                            setGeneraOssIdsLunghe((prev) =>
                              checked ? [...prev, o.id] : prev.filter((id) => id !== o.id)
                            );
                            if (checked) {
                              setGeneraOssIdsSoloMattina((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsSoloPomeriggio((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsSoloNotte((prev) => prev.filter((id) => id !== o.id));
                            }
                          }}
                        />
                        <span className="text-sm">{o.nome} {o.cognome}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {generaOssIds.length > 0 && (
                <div className="md:col-span-2 lg:col-span-1">
                  <Label className="text-slate-700 font-medium">Non presenti di domenica</Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    Gli OSS selezionati non ricevono alcun turno nelle date di domenica del mese: né mattina, né pomeriggio, né notte (incluso il turno notte registrato con data domenica).
                  </p>
                  <div className="max-h-[200px] overflow-y-auto rounded-md border border-slate-200 p-2.5 space-y-1.5 bg-slate-50/50">
                    {ossOnlyList.filter((o) => generaOssIds.includes(o.id)).map((o) => (
                      <label
                        key={o.id}
                        className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white cursor-pointer"
                      >
                        <Checkbox
                          checked={generaOssIdsAssentiDomenica.includes(o.id)}
                          onCheckedChange={(checked) => {
                            setGeneraOssIdsAssentiDomenica((prev) =>
                              checked ? [...prev, o.id] : prev.filter((id) => id !== o.id)
                            );
                          }}
                        />
                        <span className="text-sm">{o.nome} {o.cognome}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {generaOssIds.length > 0 && generaModalita === "copertura" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-4 border-t border-slate-200">
                <div>
                  <Label className="text-slate-700 font-medium">Solo mattina ({formatTurnoRange(turnoOrari.mattina)})</Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    Questi OSS ricevono solo turni mattina nella generazione automatica (mai pomeriggio, notte o lunga). Un OSS può essere in una sola tra solo mattina, solo pomeriggio e solo notte.
                  </p>
                  <div className="max-h-[200px] overflow-y-auto rounded-md border border-slate-200 p-2.5 space-y-1.5 bg-slate-50/50">
                    {ossOnlyList.filter((o) => generaOssIds.includes(o.id)).map((o) => (
                      <label
                        key={`solo-m-${o.id}`}
                        className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white cursor-pointer"
                      >
                        <Checkbox
                          checked={generaOssIdsSoloMattina.includes(o.id)}
                          disabled={generaOssIdsLunghe.includes(o.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setGeneraOssIdsSoloMattina((prev) => (prev.includes(o.id) ? prev : [...prev, o.id]));
                              setGeneraOssIdsSoloPomeriggio((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsSoloNotte((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsLunghe((prev) => prev.filter((id) => id !== o.id));
                            } else {
                              setGeneraOssIdsSoloMattina((prev) => prev.filter((id) => id !== o.id));
                            }
                          }}
                        />
                        <span className="text-sm">{o.nome} {o.cognome}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Solo pomeriggio ({formatTurnoRange(turnoOrari.pomeriggio)})</Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    Solo turni pomeriggio (mai mattina, notte o lunga).
                  </p>
                  <div className="max-h-[200px] overflow-y-auto rounded-md border border-slate-200 p-2.5 space-y-1.5 bg-slate-50/50">
                    {ossOnlyList.filter((o) => generaOssIds.includes(o.id)).map((o) => (
                      <label
                        key={`solo-p-${o.id}`}
                        className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white cursor-pointer"
                      >
                        <Checkbox
                          checked={generaOssIdsSoloPomeriggio.includes(o.id)}
                          disabled={generaOssIdsLunghe.includes(o.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setGeneraOssIdsSoloPomeriggio((prev) => (prev.includes(o.id) ? prev : [...prev, o.id]));
                              setGeneraOssIdsSoloMattina((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsSoloNotte((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsLunghe((prev) => prev.filter((id) => id !== o.id));
                            } else {
                              setGeneraOssIdsSoloPomeriggio((prev) => prev.filter((id) => id !== o.id));
                            }
                          }}
                        />
                        <span className="text-sm">{o.nome} {o.cognome}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Solo notte ({formatTurnoRange(turnoOrari.notte)})</Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    Solo turni notte (mai mattina, pomeriggio o lunga).
                  </p>
                  <div className="max-h-[200px] overflow-y-auto rounded-md border border-slate-200 p-2.5 space-y-1.5 bg-slate-50/50">
                    {ossOnlyList.filter((o) => generaOssIds.includes(o.id)).map((o) => (
                      <label
                        key={`solo-n-${o.id}`}
                        className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white cursor-pointer"
                      >
                        <Checkbox
                          checked={generaOssIdsSoloNotte.includes(o.id)}
                          disabled={generaOssIdsLunghe.includes(o.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setGeneraOssIdsSoloNotte((prev) => (prev.includes(o.id) ? prev : [...prev, o.id]));
                              setGeneraOssIdsSoloMattina((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsSoloPomeriggio((prev) => prev.filter((id) => id !== o.id));
                              setGeneraOssIdsLunghe((prev) => prev.filter((id) => id !== o.id));
                            } else {
                              setGeneraOssIdsSoloNotte((prev) => prev.filter((id) => id !== o.id));
                            }
                          }}
                        />
                        <span className="text-sm">{o.nome} {o.cognome}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Opzioni avanzate (solo modalità copertura) */}
            {generaModalita === "copertura" && (
            <div className="border-t border-slate-200 pt-4">
              <button
                type="button"
                className="text-sm font-medium text-sky-600 hover:text-sky-700 hover:underline flex items-center gap-1"
                onClick={() => setGeneraAvanzate((a) => !a)}
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${generaAvanzate ? "rotate-180" : ""}`} />
                Opzioni avanzate
              </button>
              {generaAvanzate && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 rounded-lg border border-slate-200 p-4 bg-slate-50">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">Max notti consecutive</Label>
                    <p className="text-xs text-slate-500">Numero massimo di notti di fila che un OSS può fare prima dello smonto notte/riposo (es. 3 = dopo 3 notti consecutive).</p>
                    <Input
                      type="number"
                      min={1}
                      max={7}
                      value={generaMaxNotti}
                      onChange={(e) => setGeneraMaxNotti(parseInt(e.target.value, 10) || 1)}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">Smonto/riposo dopo notti (giorni)</Label>
                    <p className="text-xs text-slate-500">Giorni non assegnabili dopo una sequenza di notti: il primo viene indicato come smonto notte, gli eventuali successivi come riposo.</p>
                    <Input
                      type="number"
                      min={0}
                      max={3}
                      value={generaRiposo}
                      onChange={(e) => setGeneraRiposo(parseInt(e.target.value, 10) || 0)}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">Lunghe ({formatTurnoRange(turnoOrari.lunga)}) per giorno</Label>
                    <p className="text-xs text-slate-500">Quanti OSS possono fare il turno lungo mattina+pomeriggio nello stesso giorno. Ogni lunga copre 1 slot mattina e 1 pomeriggio; gli altri slot si riempiono separatamente.</p>
                    <Input
                      type="number"
                      min={0}
                      value={generaTurniLunghe}
                      onChange={(e) => setGeneraTurniLunghe(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">Riposo dopo lunga (giorni)</Label>
                    <p className="text-xs text-slate-500">Giorni di riposo dopo un turno lungo ({formatTurnoRange(turnoOrari.lunga)}); in questi giorni l&apos;OSS non viene assegnato a nessun turno.</p>
                    <Input
                      type="number"
                      min={0}
                      max={3}
                      value={generaRiposoDopoLunga}
                      onChange={(e) => setGeneraRiposoDopoLunga(parseInt(e.target.value, 10) || 0)}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">OSS mattina ({formatTurnoRange(turnoOrari.mattina)}) per giorno</Label>
                    <p className="text-xs text-slate-500">Numero di OSS da assegnare ogni giorno al turno mattina. Le lunghe già coprono parte di questi slot.</p>
                    <Input
                      type="number"
                      min={1}
                      value={generaTurniMattina}
                      onChange={(e) => setGeneraTurniMattina(parseInt(e.target.value, 10) || 1)}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">OSS pomeriggio ({formatTurnoRange(turnoOrari.pomeriggio)}) per giorno</Label>
                    <p className="text-xs text-slate-500">Numero di OSS da assegnare ogni giorno al turno pomeriggio. Le lunghe già coprono parte di questi slot.</p>
                    <Input
                      type="number"
                      min={1}
                      value={generaTurniPomeriggio}
                      onChange={(e) => setGeneraTurniPomeriggio(parseInt(e.target.value, 10) || 1)}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="space-y-1 sm:col-span-2 lg:col-span-1">
                    <Label className="text-sm font-medium text-slate-700">OSS notte ({formatTurnoRange(turnoOrari.notte)}) per giorno</Label>
                    <p className="text-xs text-slate-500">Numero di OSS da assegnare ogni giorno al turno notte.</p>
                    <Input
                      type="number"
                      min={1}
                      value={generaTurniNotte}
                      onChange={(e) => setGeneraTurniNotte(parseInt(e.target.value, 10) || 1)}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="space-y-3 sm:col-span-2 lg:col-span-3 border-t border-slate-300/80 pt-4">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <Checkbox
                        className="mt-0.5"
                        checked={generaCoperturaFestivi}
                        onCheckedChange={(c) => {
                          const on = c === true;
                          setGeneraCoperturaFestivi(on);
                          if (on) {
                            setGeneraTurniMattinaFestivo(generaTurniMattina);
                            setGeneraTurniPomeriggioFestivo(generaTurniPomeriggio);
                            setGeneraTurniNotteFestivo(generaTurniNotte);
                            setGeneraTurniLungheFestivo(generaTurniLunghe);
                          }
                        }}
                      />
                      <span>
                        <span className="text-sm font-medium text-slate-700">Copertura diversa nei giorni festivi</span>
                        <p className="text-xs text-slate-500 font-normal mt-0.5">
                          Domeniche e festività nazionali italiane (Capodanno, Epifania, Pasqua e Pasquetta, 25 aprile, 1° maggio, 2 giugno, Ferragosto, Ognissanti, Immacolata, Natale, Santo Stefano). All&apos;attivazione i valori copiano quelli feriali; puoi ridurli (anche a 0 per un turno).
                        </p>
                      </span>
                    </label>
                    {generaCoperturaFestivi && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-sm font-medium text-slate-700">OSS mattina festivi</Label>
                          <Input
                            type="number"
                            min={0}
                            value={generaTurniMattinaFestivo}
                            onChange={(e) =>
                              setGeneraTurniMattinaFestivo(Math.max(0, parseInt(e.target.value, 10) || 0))
                            }
                            className="mt-1 h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-medium text-slate-700">OSS pomeriggio festivi</Label>
                          <Input
                            type="number"
                            min={0}
                            value={generaTurniPomeriggioFestivo}
                            onChange={(e) =>
                              setGeneraTurniPomeriggioFestivo(Math.max(0, parseInt(e.target.value, 10) || 0))
                            }
                            className="mt-1 h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-medium text-slate-700">OSS notte festivi</Label>
                          <Input
                            type="number"
                            min={0}
                            value={generaTurniNotteFestivo}
                            onChange={(e) =>
                              setGeneraTurniNotteFestivo(Math.max(0, parseInt(e.target.value, 10) || 0))
                            }
                            className="mt-1 h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-medium text-slate-700">Lunghe ({formatTurnoRange(turnoOrari.lunga)}) festivi</Label>
                          <Input
                            type="number"
                            min={0}
                            value={generaTurniLungheFestivo}
                            onChange={(e) =>
                              setGeneraTurniLungheFestivo(Math.max(0, parseInt(e.target.value, 10) || 0))
                            }
                            className="mt-1 h-9"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
          {/* Istruzioni aggiuntive — solo in generazione, se abilitata in admin */}
          {turniAutoDialogMode === "genera" && turniConfig.ai_generazione_turni_enabled && (
            <div className="border-t pt-4 mt-2 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    Istruzioni aggiuntive
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Integra le regole sopra con indicazioni in linguaggio naturale (es. ferie per nome, preferenze su turni o weekend).
                  </p>
                </div>
                <Switch
                  checked={generaUsaIstruzioni}
                  onCheckedChange={setGeneraUsaIstruzioni}
                />
              </div>
              {generaUsaIstruzioni && (
                <textarea
                  className="w-full min-h-[110px] rounded-md border border-violet-200 bg-violet-50/40 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-y"
                  placeholder={`Esempi:\n- Maria non fa mai turni di notte\n- Nei weekend preferire Luigi e Anna per le mattine\n- Distribuire le lunghe equamente tra tutti\n- Riposo ogni 5 turni`}
                  value={generaIstruzioniNote}
                  onChange={(e) => setGeneraIstruzioniNote(e.target.value.slice(0, 2000))}
                  maxLength={2000}
                />
              )}
              {generaUsaIstruzioni && !generaIstruzioniNote.trim() && (
                <p className="text-xs text-amber-600">Inserisci almeno un&apos;istruzione per usarla in generazione, altrimenti verrà usata la generazione standard.</p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGeneraTurniModal(false)}>
              Annulla
            </Button>
            {turniAutoDialogMode === "controlla" ? (
              <Button
                onClick={handleControllaTurni}
                disabled={loadingControlla || generaOssIds.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loadingControlla ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-1" />}
                Esegui controllo
              </Button>
            ) : (
              <Button onClick={handleGeneraTurni} disabled={loadingGenera || generaOssIds.length === 0} className="bg-sky-600 hover:bg-sky-700">
                {loadingGenera ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {generaUsaIstruzioni && generaIstruzioniNote.trim() ? "Genera con istruzioni" : "Genera"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showGeneraAttesaModal}
        onOpenChange={(open) => {
          if (!open && !loadingGenera) setShowGeneraAttesaModal(false);
        }}
      >
        <DialogContent
          className="max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-violet-600 shrink-0" />
              Generazione in corso
            </DialogTitle>
            <DialogDescription className="text-left pt-1 space-y-2">
              <p>
                L&apos;operazione avanzata con istruzioni aggiuntive può richiedere alcuni minuti.
              </p>
              <p className="text-slate-600">Attendere prego, non chiudere la pagina.</p>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={showControllaRisultatiModal} onOpenChange={setShowControllaRisultatiModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
              Esito controllo turni
            </DialogTitle>
            <DialogDescription>
              {controllaRisultati?.summary || "Verifica completata."}
            </DialogDescription>
          </DialogHeader>
          {controllaRisultati && (
            <div className="space-y-4 py-2">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant={controllaRisultati.conteggi?.errori ? "destructive" : "secondary"}>
                  {controllaRisultati.conteggi?.errori || 0} errori
                </Badge>
                <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">
                  {controllaRisultati.conteggi?.avvisi || 0} avvisi
                </Badge>
                {controllaRisultati.ok && (
                  <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100">Nessun errore bloccante</Badge>
                )}
              </div>

              {(controllaRisultati.problemi || []).length > 0 ? (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
                  {controllaRisultati.problemi.map((p, idx) => (
                    <div key={`${p.categoria}-${idx}`} className="flex gap-2.5 px-3 py-2.5 text-sm">
                      <span
                        className={`shrink-0 mt-0.5 w-2 h-2 rounded-full ${
                          p.livello === "errore"
                            ? "bg-red-500"
                            : p.livello === "avviso"
                              ? "bg-amber-500"
                              : "bg-slate-400"
                        }`}
                        aria-hidden
                      />
                      <div>
                        <span className="text-xs uppercase tracking-wide text-slate-400">{p.categoria}</span>
                        <p className="text-slate-800">{p.messaggio}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  I turni del mese risultano coerenti con le impostazioni indicate e ben distribuiti tra gli OSS selezionati.
                </p>
              )}

              {(controllaRisultati.statistiche?.per_oss || []).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">Turni per operatore</p>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Operatore</th>
                          <th className="text-center px-2 py-2 font-medium">Tot.</th>
                          <th className="text-center px-2 py-2 font-medium">M</th>
                          <th className="text-center px-2 py-2 font-medium">P</th>
                          <th className="text-center px-2 py-2 font-medium">N</th>
                        </tr>
                      </thead>
                      <tbody>
                        {controllaRisultati.statistiche.per_oss.map((row) => (
                          <tr key={row.oss_id} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-800">{row.nome}</td>
                            <td className="text-center px-2 py-1.5 font-medium">{row.totale}</td>
                            <td className="text-center px-2 py-1.5">{row.mattina}</td>
                            <td className="text-center px-2 py-1.5">{row.pomeriggio}</td>
                            <td className="text-center px-2 py-1.5">{row.notte}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowControllaRisultatiModal(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="candidati" className="mt-0">
      {/* SEZIONE: LE MIE RICHIESTE (solo se piano attivo) */}
      {userPlan && (
        <Card className="card-base mb-6 border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-violet-700">
                <Send className="w-5 h-5" />
                Le Mie Richieste
              </CardTitle>
              <Button 
                onClick={() => setShowNewRequestModal(true)}
                className="bg-violet-600 hover:bg-violet-700"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nuova richiesta
              </Button>
            </div>
            <p className="text-sm text-violet-600">
              Gestisci le tue richieste di personale
            </p>
          </CardHeader>
          <CardContent>
            {loadingRequests ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-6">
                <Send className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">Nessuna richiesta inviata</p>
                <p className="text-sm text-slate-400">Crea la tua prima richiesta di personale</p>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((req) => {
                  const stato = STATI_RICHIESTA[req.stato] || STATI_RICHIESTA.inviata;
                  const StatoIcon = stato.icon;
                  return (
                    <button
                      type="button"
                      key={req.id}
                      onClick={() => setSelectedRequest(req)}
                      className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50/50 transition-colors text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{req.ruolo}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(req.created_at).toLocaleDateString('it-IT')}
                            {req.urgenza !== "normale" && (
                              <span className="ml-2 text-amber-600">• {req.urgenza}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Badge className={stato.color}>
                        <StatoIcon className="w-3 h-3 mr-1" />
                        {stato.label}
                        {req.cv_ricevuti?.length > 0 && ` (${req.cv_ricevuti.length})`}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog Dettaglio richiesta */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <Send className="w-5 h-5" />
              Dettaglio richiesta
            </DialogTitle>
            <DialogDescription>
              Tutte le informazioni della richiesta di personale
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-sm items-baseline">
                <span className="text-slate-500 font-medium">Ruolo</span>
                <span className="text-slate-900">{selectedRequest.ruolo}</span>
                <span className="text-slate-500 font-medium">Stato</span>
                <div>
                  {(() => {
                    const s = STATI_RICHIESTA[selectedRequest.stato] || STATI_RICHIESTA.inviata;
                    const Icon = s.icon;
                    return (
                      <Badge className={s.color}>
                        <Icon className="w-3 h-3 mr-1" />
                        {s.label}
                      </Badge>
                    );
                  })()}
                </div>
                <span className="text-slate-500 font-medium">Urgenza</span>
                <span className="text-slate-900 capitalize">{selectedRequest.urgenza}</span>
                {selectedRequest.orario && (
                  <>
                    <span className="text-slate-500 font-medium">Orario</span>
                    <span className="text-slate-900">{selectedRequest.orario}</span>
                  </>
                )}
                {selectedRequest.data_inizio && (
                  <>
                    <span className="text-slate-500 font-medium">Data inizio</span>
                    <span className="text-slate-900">{selectedRequest.data_inizio}</span>
                  </>
                )}
                <span className="text-slate-500 font-medium">Data creazione</span>
                <span className="text-slate-900">
                  {new Date(selectedRequest.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-slate-500 font-medium">Ultimo aggiornamento</span>
                <span className="text-slate-900">
                  {new Date(selectedRequest.updated_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                {selectedRequest.cv_ricevuti?.length > 0 && (
                  <>
                    <span className="text-slate-500 font-medium">CV ricevuti</span>
                    <span className="text-slate-900">{selectedRequest.cv_ricevuti.length} profilo/i</span>
                  </>
                )}
                {selectedRequest.struttura_nome && (
                  <>
                    <span className="text-slate-500 font-medium">Struttura</span>
                    <span className="text-slate-900">{selectedRequest.struttura_nome}</span>
                  </>
                )}
                {selectedRequest.piano && (
                  <>
                    <span className="text-slate-500 font-medium">Piano</span>
                    <span className="text-slate-900 capitalize">{selectedRequest.piano}</span>
                  </>
                )}
              </div>
              {selectedRequest.descrizione && (
                <div>
                  <p className="text-slate-500 font-medium text-sm mb-1">Descrizione</p>
                  <p className="text-slate-700 text-sm whitespace-pre-wrap rounded-lg bg-slate-50 p-3">{selectedRequest.descrizione}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEZIONE 1: CV GRATUITI */}
      <Card className="card-base mb-6 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <Sparkles className="w-5 h-5" />
              Profili attualmente disponibili – Accesso gratuito
            </CardTitle>
            <Badge className="bg-emerald-500 text-white">Incluso con Affidya</Badge>
          </div>
          <p className="text-sm text-emerald-600">
            Visualizza i profili presenti nel sistema e compatibili con la tua zona (raggio {raggioKm} km).
          </p>
        </CardHeader>
        <CardContent>
          {loadingCandidati ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : candidati.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun candidato disponibile nella tua zona</p>
              <p className="text-sm text-slate-400 mt-1">I candidati verranno mostrati quando disponibili</p>
            </div>
          ) : (
            <div className="space-y-3">
              {candidati.map((candidato) => (
                <div 
                  key={candidato.id}
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-100 hover:border-emerald-200 hover:shadow-sm transition-all cursor-pointer"
                  data-testid={`candidato-${candidato.id}`}
                  onClick={() => openCandidatoDetails(candidato)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {candidato.nome} {candidato.cognome}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                        {candidato.qualifica && (
                          <Badge variant="outline" className="text-xs">
                            {candidato.qualifica}
                          </Badge>
                        )}
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {candidato.zone_ricerca || "N/D"}
                        </span>
                        {candidato.distanza_km && (
                          <span className="text-emerald-600 font-medium">
                            ~{candidato.distanza_km} km
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  >
                    Dettagli
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SEZIONE 2: PIANI A PAGAMENTO (solo se non ha piano) */}
      {!userPlan && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-slate-900">
              Potenzia la ricerca con le Richieste Personale
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Piano BASE */}
            <Card className="card-base border-2 border-blue-200 hover:border-blue-400 transition-all flex flex-col h-full">
              <CardContent className="p-4 sm:p-6 flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-blue-100 text-blue-700">BASE</Badge>
                  <div className="text-right">
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">49€</p>
                    <p className="text-xs sm:text-sm text-slate-500">/mese</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-4 text-xs sm:text-sm flex-grow">
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <span>Creazione e pubblicazione annunci di lavoro</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <span>Attivazione ricerca su più canali professionali</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <span>Raccolta candidature da sito, email e annunci</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <span>Selezione e tracciamento dei profili compatibili</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <span>Stato avanzamento richieste e storico selezioni</span>
                  </li>
                </ul>
                <div className="bg-blue-50 rounded-lg p-3 mb-4 text-xs text-blue-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Attivando il piano avrai accesso alla sezione <strong>Le Mie Richieste</strong> per inviare richieste di personale e ricevere CV mirati.
                </div>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 mt-auto"
                  onClick={() => handleSelectPlan('BASE')}
                  data-testid="btn-piano-base"
                >
                  Attiva Piano Base
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Piano PRO */}
            <Card className="card-base border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white hover:border-amber-400 transition-all relative overflow-hidden flex flex-col h-full">
              <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs px-2 sm:px-3 py-1 rounded-bl-lg font-semibold">
                CONSIGLIATO
              </div>
              <CardContent className="p-4 sm:p-6 flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    PRO
                  </Badge>
                  <div className="text-right">
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">89€</p>
                    <p className="text-xs sm:text-sm text-slate-500">/mese</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-4 text-xs sm:text-sm flex-grow">
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>Priorità nella presa in carico delle richieste</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>Pubblicazione annunci su più canali di ricerca lavoro</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>Selezione avanzata dei profili disponibili</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>Riduzione dei tempi di ricerca</span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>Supporto dedicato nella gestione delle candidature</span>
                  </li>
                </ul>
                <div className="bg-amber-50 rounded-lg p-3 mb-4 text-xs text-amber-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Attivando il piano avrai accesso alla sezione <strong>Le Mie Richieste</strong> per inviare richieste di personale e ricevere CV mirati.
                </div>
                <Button 
                  className="w-full bg-amber-500 hover:bg-amber-600 mt-auto"
                  onClick={() => handleSelectPlan('PRO')}
                  data-testid="btn-piano-pro"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Attiva Piano PRO
                </Button>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-4 space-y-1">
            <p className="text-sm text-slate-500">
              Disdici quando vuoi. Nessun vincolo.
            </p>
            <p className="text-xs text-slate-400 max-w-xl mx-auto">
              La disponibilità dei profili dipende dal territorio e dal periodo.
              Affidya fornisce un sistema strutturato di ricerca del personale.
            </p>
          </div>
        </div>
      )}

      {/* SEZIONE 3: FAQ - Collapsible */}
      <Card className="card-base border-2 border-slate-200 mb-6">
        <button
          onClick={() => setShowFaq(!showFaq)}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors rounded-t-xl"
        >
          <div className="flex items-center gap-2 text-slate-700">
            <HelpCircle className="w-5 h-5" />
            <span className="font-semibold">FAQ – Perché non vedo candidati?</span>
          </div>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showFaq ? 'rotate-180' : ''}`} />
        </button>
        
        {showFaq && (
          <CardContent className="pt-0 border-t border-slate-100">
            <div className="space-y-2 pt-3">
              {faqItems.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-slate-100 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-medium text-slate-700 text-sm pr-4">{faq.question}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-3 pb-3 text-sm text-slate-600 bg-slate-50 border-t border-slate-100">
                      <div className="pt-2 space-y-1">
                        {faq.answer.map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

        </TabsContent>
      </Tabs>

      <StaffDocumentiDialog
        open={!!documentiStaff}
        onOpenChange={(open) => {
          if (!open) setDocumentiStaff(null);
        }}
        staff={documentiStaff}
        token={token}
        canEdit={hasPermission("users:update")}
      />

      {/* Modal Aggiungi OSS */}
      <Dialog
        open={showOssModal}
        onOpenChange={(open) => {
          setShowOssModal(open);
          if (!open) setEditingOssId(null);
        }}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-[720px] max-h-[min(90dvh,720px)] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 pr-12">
            <DialogTitle className="flex items-center gap-2">
              {editingOssId ? (
                <Pencil className="w-5 h-5 text-sky-600" />
              ) : (
                <StaffFiguraIcon figura={ossForm.figura} className="w-5 h-5" />
              )}
              {editingOssId
                ? `Modifica ${staffFiguraLabel(ossForm.figura)}`
                : `Aggiungi ${staffFiguraLabel(ossForm.figura)}`}
            </DialogTitle>
            <DialogDescription>
              {editingOssId
                ? isOssFigura(ossForm)
                  ? "Aggiorna anagrafica e credenziali. Lascia la password vuota per non modificarla."
                  : isFiguraAttivita(ossForm)
                    ? "Aggiorna anagrafica, specializzazione e email per l'invio dei turni."
                    : "Aggiorna anagrafica. L'email è opzionale e serve solo per l'invio dei turni."
                : isOssFigura(ossForm)
                  ? "Scegli come accedere all'area riservata: email + password oppure codice univoco + password."
                  : isFiguraAttivita(ossForm)
                    ? "Nome, cognome, email e specializzazione per il calendario. Nessun accesso alla piattaforma."
                    : "Nome e cognome per il calendario turni. Email opzionale per l'invio dei turni. Nessun accesso alla piattaforma."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 px-6 py-2 overflow-y-auto flex-1 min-h-0">
            {/* Colonna sinistra: anagrafica */}
            <div className="space-y-4 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Anagrafica</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="oss-nome">Nome *</Label>
                  <Input
                    id="oss-nome"
                    value={ossForm.nome}
                    onChange={(e) => setOssForm((p) => ({ ...p, nome: e.target.value }))}
                    placeholder="Mario"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="oss-cognome">Cognome *</Label>
                  <Input
                    id="oss-cognome"
                    value={ossForm.cognome}
                    onChange={(e) => setOssForm((p) => ({ ...p, cognome: e.target.value }))}
                    placeholder="Rossi"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="oss-telefono">Telefono</Label>
                <Input
                  id="oss-telefono"
                  type="tel"
                  value={ossForm.telefono}
                  onChange={(e) => setOssForm((p) => ({ ...p, telefono: e.target.value }))}
                  placeholder="333 1234567"
                  className="mt-1"
                />
              </div>
              {isOssFigura(ossForm) && ossForm.auth_mode === "code" ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-900">
                    <KeyRound className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-medium">Codice di accesso</span>
                  </div>
                  {editingOssId && ossForm.login_code && !ossForm.regenerate_login_code ? (
                    <>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm font-mono tracking-wider bg-white border border-amber-100 rounded px-3 py-2 text-slate-900">
                          {ossForm.login_code}
                        </code>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(ossForm.login_code).then(
                              () => toast.success("Codice copiato"),
                              () => toast.error("Impossibile copiare")
                            );
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto px-0 text-amber-800 hover:text-amber-900 hover:bg-transparent"
                        onClick={() =>
                          setOssForm((p) => ({ ...p, regenerate_login_code: true }))
                        }
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                        Genera un nuovo codice al salvataggio
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-amber-900/90">
                      {editingOssId && ossForm.regenerate_login_code
                        ? "Al salvataggio verrà generato un nuovo codice (il precedente non sarà più valido)."
                        : "Il codice verrà generato automaticamente al salvataggio e mostrato a schermo."}
                    </p>
                  )}
                  {editingOssId && ossForm.regenerate_login_code ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-0 text-slate-600"
                      onClick={() => setOssForm((p) => ({ ...p, regenerate_login_code: false }))}
                    >
                      Annulla rigenerazione
                    </Button>
                  ) : null}
                  <div>
                    <Label htmlFor="oss-email-opt">Email (opzionale, per i turni)</Label>
                    <Input
                      id="oss-email-opt"
                      type="email"
                      value={ossForm.email}
                      onChange={(e) => setOssForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder="Opzionale"
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="oss-email">
                    {emailOptionalForFigura(ossForm)
                      ? "Email (opzionale, per i turni)"
                      : "Email *"}
                  </Label>
                  <Input
                    id="oss-email"
                    type="email"
                    value={ossForm.email}
                    onChange={(e) => setOssForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder={emailOptionalForFigura(ossForm) ? "Opzionale" : "mario.rossi@email.it"}
                    className="mt-1"
                  />
                </div>
              )}
              {isFiguraAttivita(ossForm) ? (
                <div className="space-y-3">
                  <div>
                    <Label>Specializzazione *</Label>
                    <Select
                      value={ossForm.specializzazione || ""}
                      onValueChange={(v) =>
                        setOssForm((p) => ({
                          ...p,
                          specializzazione: v,
                          specializzazione_altro: v === "altro" ? p.specializzazione_altro : "",
                        }))
                      }
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue placeholder="Seleziona specializzazione" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffSpecializzazioneOptions(ossForm.figura).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {ossForm.specializzazione === "altro" ? (
                    <div>
                      <Label htmlFor="oss-spec-altro">Specifica specializzazione *</Label>
                      <Input
                        id="oss-spec-altro"
                        value={ossForm.specializzazione_altro}
                        onChange={(e) =>
                          setOssForm((p) => ({ ...p, specializzazione_altro: e.target.value }))
                        }
                        placeholder="Es. Educatore musicale"
                        className="mt-1"
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Colonna destra: accesso e opzioni */}
            <div className="space-y-4 min-w-0">
              {isOssFigura(ossForm) ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Accesso area OSS</p>
                  <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                    <Label className="text-sm font-medium text-slate-800">Modalità di accesso *</Label>
                    <RadioGroup
                      value={ossForm.auth_mode || "email"}
                      onValueChange={(v) =>
                        setOssForm((p) => ({
                          ...p,
                          auth_mode: v,
                          invia_email_accesso: v === "email" ? p.invia_email_accesso : false,
                          regenerate_login_code: false,
                        }))
                      }
                      className="gap-2"
                    >
                      <label
                        htmlFor="oss-auth-email"
                        className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer ${
                          ossForm.auth_mode === "email" ? "border-sky-400 bg-sky-50/60" : "border-slate-200"
                        }`}
                      >
                        <RadioGroupItem value="email" id="oss-auth-email" className="mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">Email + password</p>
                          <p className="text-xs text-slate-500">L&apos;operatore accede con la propria email</p>
                        </div>
                      </label>
                      <label
                        htmlFor="oss-auth-code"
                        className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer ${
                          ossForm.auth_mode === "code" ? "border-sky-400 bg-sky-50/60" : "border-slate-200"
                        }`}
                      >
                        <RadioGroupItem value="code" id="oss-auth-code" className="mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">Codice univoco + password</p>
                          <p className="text-xs text-slate-500">
                            Affidya genera un codice da comunicare all&apos;operatore
                          </p>
                        </div>
                      </label>
                    </RadioGroup>
                  </div>
                  <div>
                    <Label htmlFor="oss-password">{editingOssId ? "Nuova password (opzionale)" : "Password *"}</Label>
                    <Input
                      id="oss-password"
                      type="password"
                      value={ossForm.password}
                      onChange={(e) => setOssForm((p) => ({ ...p, password: e.target.value }))}
                      placeholder={editingOssId ? "Lascia vuoto per non cambiare" : "Min. 8 caratteri"}
                      className="mt-1"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-md border border-amber-100 bg-amber-50/50 p-3">
                    <div className="min-w-0">
                      <Label htmlFor="oss-responsabile" className="text-sm font-medium leading-tight">
                        Responsabile
                      </Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Turni di tutti, Risparmio, medici e manutentori; Manutenzioni se attiva.
                      </p>
                    </div>
                    <Switch
                      id="oss-responsabile"
                      checked={!!ossForm.responsabile}
                      onCheckedChange={(v) => setOssForm((p) => ({ ...p, responsabile: v === true }))}
                      className="shrink-0"
                    />
                  </div>
                  {ossForm.auth_mode === "email" ? (
                    <div className="flex items-start gap-2 rounded-md border border-sky-100 bg-sky-50/50 p-3">
                      <Checkbox
                        id="oss-invia-email"
                        checked={!!ossForm.invia_email_accesso}
                        onCheckedChange={(v) => setOssForm((p) => ({ ...p, invia_email_accesso: v === true }))}
                      />
                      <div className="space-y-0.5 min-w-0">
                        <Label htmlFor="oss-invia-email" className="text-sm font-medium cursor-pointer leading-tight">
                          {editingOssId ? "Invia email di accesso" : "Invia email con link e password"}
                        </Label>
                        <p className="text-xs text-slate-500">
                          {editingOssId
                            ? "Promemoria con link; se imposti una nuova password, sarà inclusa."
                            : "La password viene inviata una sola volta nell'email."}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-slate-500 rounded-lg border border-dashed border-slate-200 p-4">
                  Questa figura non ha accesso all&apos;area OSS. Compila solo i dati anagrafici a sinistra.
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
            <Button
              variant="outline"
              onClick={() => {
                setShowOssModal(false);
                setEditingOssId(null);
              }}
            >
              Annulla
            </Button>
            <Button
              className={getStaffFiguraTheme(ossForm.figura).btnClass}
              onClick={handleSaveOss}
              disabled={
                submittingOss ||
                !ossForm.nome?.trim() ||
                !ossForm.cognome?.trim() ||
                (isOssFigura(ossForm)
                  ? ossForm.auth_mode === "email"
                    ? !ossForm.email?.trim()
                    : false
                  : emailOptionalForFigura(ossForm)
                    ? false
                    : !ossForm.email?.trim()) ||
                (!editingOssId && isOssFigura(ossForm) && !ossForm.password?.trim()) ||
                (isFiguraAttivita(ossForm) &&
                  (!ossForm.specializzazione ||
                    (ossForm.specializzazione === "altro" && !ossForm.specializzazione_altro?.trim())))
              }
            >
              {submittingOss ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingOssId ? "Salva modifiche" : `Aggiungi ${staffFiguraLabel(ossForm.figura)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog codice accesso OSS appena generato */}
      <Dialog open={!!ossCodeReveal} onOpenChange={(open) => !open && setOssCodeReveal(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-sky-600" />
              Codice di accesso OSS
            </DialogTitle>
            <DialogDescription>
              Comunica questo codice a{" "}
              <span className="font-medium text-slate-800">
                {ossCodeReveal?.nome} {ossCodeReveal?.cognome}
              </span>{" "}
              insieme alla password. Serve per accedere a{" "}
              <span className="font-medium">/oss-login</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <code className="flex-1 text-xl font-mono tracking-widest text-center bg-slate-50 border rounded-lg px-4 py-3 text-slate-900">
              {ossCodeReveal?.login_code}
            </code>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (!ossCodeReveal?.login_code) return;
                navigator.clipboard.writeText(ossCodeReveal.login_code).then(
                  () => toast.success("Codice copiato"),
                  () => toast.error("Impossibile copiare")
                );
              }}
            >
              <Copy className="w-4 h-4 mr-1" />
              Copia
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Il codice resta visibile anche in modifica sull&apos;elenco OSS. Se lo rigeneri, il precedente non sarà più
            valido.
          </p>
          <DialogFooter>
            <Button onClick={() => setOssCodeReveal(null)} className="bg-sky-600 hover:bg-sky-700">
              Ho annotato il codice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Dettagli Candidato */}
      <Dialog open={!!selectedCandidato} onOpenChange={() => setSelectedCandidato(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <User className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <span className="block">
                  {selectedCandidato?.nome} {selectedCandidato?.cognome}
                </span>
                {selectedCandidato?.qualifica && (
                  <Badge variant="outline" className="mt-1 text-xs font-normal">
                    {selectedCandidato.qualifica}
                  </Badge>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedCandidato && (
            <div className="py-4 space-y-4">
              {/* Zona e distanza */}
              <div className="flex items-center gap-2 text-slate-600 bg-slate-50 p-3 rounded-lg">
                <MapPin className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium">{selectedCandidato.zone_ricerca || "Non specificata"}</p>
                  {selectedCandidato.distanza_km && (
                    <p className="text-sm text-emerald-600">~{selectedCandidato.distanza_km} km dalla tua struttura</p>
                  )}
                </div>
              </div>

              {/* Contatti */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Contatti</h4>
                
                {selectedCandidato.cellulare && (
                  <a 
                    href={`tel:${selectedCandidato.cellulare}`}
                    className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-500">Telefono</p>
                      <p className="font-medium text-blue-700">{selectedCandidato.cellulare}</p>
                    </div>
                  </a>
                )}

                {selectedCandidato.email && (
                  <a 
                    href={`mailto:${selectedCandidato.email}`}
                    className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-violet-600" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium text-violet-700 break-all">{selectedCandidato.email}</p>
                    </div>
                  </a>
                )}
              </div>

              {/* CV */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Curriculum</h4>
                {selectedCandidato.has_curriculum && selectedCandidato.curriculum_url ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <FileText className="w-6 h-6 text-emerald-600" />
                      <div className="flex-1">
                        <p className="font-medium text-emerald-700">
                          {selectedCandidato.curriculum_name || "Curriculum Vitae"}
                        </p>
                        <p className="text-sm text-emerald-600">CV disponibile</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => {
                          setCvUrl(`${API_URL}${selectedCandidato.curriculum_url}`);
                          setCvFileName(selectedCandidato.curriculum_name || "curriculum.pdf");
                          setShowCvModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Visualizza
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                        onClick={async () => {
                          try {
                            const response = await fetch(`${API_URL}${selectedCandidato.curriculum_url}`, {
                              headers: {
                                'Authorization': `Bearer ${token}`
                              }
                            });
                            
                            if (!response.ok) {
                              throw new Error('Download failed');
                            }
                            
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'curriculum.pdf';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (error) {
                            console.error('Download error:', error);
                            toast.error('Errore nel download del curriculum');
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Scarica
                      </Button>
                    </div>
                  </div>
                ) : selectedCandidato.curriculum_name ? (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <FileText className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-700">{selectedCandidato.curriculum_name}</p>
                      <p className="text-sm text-amber-600">CV presente ma link non disponibile</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <FileText className="w-6 h-6 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-500">Nessun CV caricato</p>
                      <p className="text-sm text-slate-400">Il candidato non ha ancora caricato il curriculum</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Data inserimento */}
              {selectedCandidato.data_inserimento && (
                <p className="text-xs text-slate-400 text-center pt-2">
                  Inserito il {new Date(selectedCandidato.data_inserimento).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedCandidato(null)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Conferma Piano */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPlan === 'PRO' ? (
                <Crown className="w-5 h-5 text-amber-500" />
              ) : (
                <Zap className="w-5 h-5 text-blue-500" />
              )}
              Attiva Piano {selectedPlan}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="text-center mb-6">
              <p className="text-4xl font-bold text-slate-900">
                {selectedPlan === 'PRO' ? '89€' : '49€'}
                <span className="text-lg font-normal text-slate-500">/mese</span>
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <h4 className="font-semibold text-slate-900 mb-2">Cosa include:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Accesso a "Le Mie Richieste"
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Invio richieste di personale
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  CV mirati per le tue esigenze
                </li>
                {selectedPlan === 'PRO' && (
                  <>
                    <li className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-amber-500" />
                      Priorità nel matching
                    </li>
                    <li className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-amber-500" />
                      Supporto dedicato
                    </li>
                  </>
                )}
              </ul>
            </div>
            <p className="text-xs text-slate-500 text-center">
              Puoi disdire in qualsiasi momento. Nessun vincolo contrattuale.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)} disabled={subscribing}>
              Annulla
            </Button>
            <Button 
              className={selectedPlan === 'PRO' ? "bg-amber-500 hover:bg-amber-600" : "bg-blue-600 hover:bg-blue-700"}
              onClick={handleActivatePlan}
              disabled={subscribing}
              data-testid="confirm-plan"
            >
              {subscribing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Procedi al pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nuova Richiesta */}
      <Dialog open={showNewRequestModal} onOpenChange={setShowNewRequestModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-violet-500" />
              Nuova Richiesta di Personale
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ruolo">Ruolo richiesto *</Label>
              <Input
                id="ruolo"
                placeholder="es. OSS, Infermiere, Fisioterapista..."
                value={newRequest.ruolo}
                onChange={(e) => setNewRequest({...newRequest, ruolo: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="descrizione">Descrizione aggiuntiva</Label>
              <Textarea
                id="descrizione"
                placeholder="Dettagli sulle competenze richieste, esperienza, ecc."
                value={newRequest.descrizione}
                onChange={(e) => setNewRequest({...newRequest, descrizione: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="urgenza">Urgenza</Label>
                <Select
                  value={newRequest.urgenza}
                  onValueChange={(value) => setNewRequest({...newRequest, urgenza: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bassa">Bassa</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="orario">Orario</Label>
                <Select
                  value={newRequest.orario}
                  onValueChange={(value) => setNewRequest({...newRequest, orario: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="turnista">Turnista</SelectItem>
                    <SelectItem value="notturno">Notturno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="data_inizio">Data inizio desiderata</Label>
              <Input
                id="data_inizio"
                type="date"
                value={newRequest.data_inizio}
                onChange={(e) => setNewRequest({...newRequest, data_inizio: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRequestModal(false)} disabled={submittingRequest}>
              Annulla
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleSubmitRequest}
              disabled={submittingRequest}
            >
              {submittingRequest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Visualizzazione CV */}
      <Dialog open={showCvModal} onOpenChange={setShowCvModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Curriculum Vitae
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 rounded-xl overflow-hidden border bg-slate-50" style={{ minHeight: '400px', maxHeight: '65vh' }}>
            {cvUrl && (
              isMobile ? (
                /* Su mobile mostra direttamente i pulsanti */
                <div className="flex flex-col items-center justify-center h-full p-8 text-center" style={{ minHeight: '400px' }}>
                  <FileText className="w-20 h-20 text-emerald-400 mb-6" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">
                    {cvFileName || "Curriculum Vitae"}
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Il documento PDF è pronto per la visualizzazione
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
                    <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        size="lg"
                        onClick={async () => {
                          try {
                            // Su Android, apri direttamente l'URL autenticato con token
                            if (isMobile && navigator.userAgent.includes('Android')) {
                              // Debug: logga il token
                              console.log('DEBUG - Token disponibile:', token ? 'SÌ' : 'NO');
                              console.log('DEBUG - Token length:', token ? token.length : 0);
                              console.log('DEBUG - CV URL base:', cvUrl);
                              
                              // Aggiungi il token come parametro query
                              const authenticatedUrl = `${cvUrl}?token=${token}`;
                              console.log('DEBUG - URL completo:', authenticatedUrl);
                              
                              // Crea un link temporaneo che triggera il download listener di Android
                              const link = document.createElement('a');
                              link.href = authenticatedUrl;
                              link.target = '_blank';
                              link.rel = 'noopener noreferrer';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            } else {
                              // Su desktop, scarica e apri con blob
                              const response = await fetch(cvUrl, {
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              });
                              
                              if (!response.ok) {
                                throw new Error('Failed to load PDF');
                              }
                              
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              
                              const pdfLink = document.createElement('a');
                              pdfLink.href = url;
                              pdfLink.target = '_blank';
                              pdfLink.rel = 'noopener noreferrer';
                              document.body.appendChild(pdfLink);
                              pdfLink.click();
                              
                              setTimeout(() => {
                                document.body.removeChild(pdfLink);
                                window.URL.revokeObjectURL(url);
                              }, 1000);
                            }
                          } catch (error) {
                            console.error('PDF view error:', error);
                            toast.error('Errore nell\'apertura del PDF');
                          }
                        }}
                      >
                        <Eye className="w-5 h-5 mr-2" />
                        Apri PDF
                      </Button>
                    <a
                      href={cvUrl}
                      download={cvFileName || 'curriculum.pdf'}
                      className="flex-1"
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Scarica
                      </Button>
                    </a>
                  </div>
                </div>
              ) : (
                /* Su desktop mostra sempre i pulsanti di fallback */
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <FileText className="w-16 h-16 text-emerald-300 mb-4" />
                  <p className="text-slate-600 mb-4">
                    Il curriculum "{cvFileName}" è pronto per la visualizzazione.
                  </p>
                  <div className="flex gap-3">
                      <Button
                        onClick={async () => {
                          try {
                            console.log('Token:', token ? 'present' : 'missing');
                            console.log('CV URL:', cvUrl);
                            
                            // Su Android, apri direttamente l'URL autenticato con token
                            if (isMobile && navigator.userAgent.includes('Android')) {
                              // Aggiungi il token come parametro query
                              const authenticatedUrl = `${cvUrl}?token=${token}`;
                              
                              // Crea un link temporaneo che triggera il download listener di Android
                              const link = document.createElement('a');
                              link.href = authenticatedUrl;
                              link.target = '_blank';
                              link.rel = 'noopener noreferrer';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            } else {
                              // Su desktop, scarica e apri con blob
                              const response = await fetch(cvUrl, {
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              });
                              
                              console.log('Response status:', response.status);
                              
                              if (!response.ok) {
                                const errorText = await response.text();
                                console.error('Error response:', errorText);
                                throw new Error('Failed to load PDF');
                              }
                              
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              
                              const pdfLink = document.createElement('a');
                              pdfLink.href = url;
                              pdfLink.target = '_blank';
                              pdfLink.rel = 'noopener noreferrer';
                              document.body.appendChild(pdfLink);
                              pdfLink.click();
                              
                              setTimeout(() => {
                                document.body.removeChild(pdfLink);
                                window.URL.revokeObjectURL(url);
                              }, 1000);
                            }
                          } catch (error) {
                            console.error('PDF view error:', error);
                            toast.error('Errore nell\'apertura del PDF');
                          }
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Apri PDF
                      </Button>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            const response = await fetch(cvUrl, {
                              headers: {
                                'Authorization': `Bearer ${token}`
                              }
                            });
                            
                            if (!response.ok) {
                              throw new Error('Download failed');
                            }
                            
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = cvFileName || 'curriculum.pdf';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (error) {
                            console.error('Download error:', error);
                            toast.error('Errore nel download del curriculum');
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Scarica
                      </Button>
                    </div>
                  </div>
              )
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowCvModal(false)}>
              Chiudi
            </Button>
            <Button 
              onClick={async () => {
                try {
                  // Su Android, apri direttamente l'URL autenticato con token
                  if (isMobile && navigator.userAgent.includes('Android')) {
                    // Aggiungi il token come parametro query
                    const authenticatedUrl = `${cvUrl}?token=${token}`;
                    
                    // Crea un link temporaneo che triggera il download listener di Android
                    const link = document.createElement('a');
                    link.href = authenticatedUrl;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  } else {
                    // Su desktop, scarica e apri con blob
                    const response = await fetch(cvUrl, {
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });
                    
                    if (!response.ok) {
                      throw new Error('Failed to load PDF');
                    }
                    
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    
                    const pdfLink = document.createElement('a');
                    pdfLink.href = url;
                    pdfLink.target = '_blank';
                    pdfLink.rel = 'noopener noreferrer';
                    document.body.appendChild(pdfLink);
                    pdfLink.click();
                    
                    setTimeout(() => {
                      document.body.removeChild(pdfLink);
                      window.URL.revokeObjectURL(url);
                    }, 1000);
                  }
                } catch (error) {
                  console.error('PDF view error:', error);
                  toast.error('Errore nell\'apertura del PDF');
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Apri in nuova scheda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
